import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { useBrowserLiveOwner } from '../hooks/useBrowserLiveOwner';
import { isBrowserLiveConversation, endLiveSession } from '../api/chatClient';
import { ChatStreamError, sendChatMessageStream, initChat, detectEntryPoint, getPersistedConversationId, setPersistedConversationId, clearPersistedConversationId, getPersistedChatOpen, setPersistedChatOpen, fetchTranscript, readReconnectIdFromUrl, stripReconnectIdFromUrl, checkReconnectAvailability } from '../api/chatClient';
import type { StreamEvent, ChatPrompt, ChatPromptResponse, Citation, EscalationClassificationPrompt, EscalationDiagnostics, EscalationEligibilityDeviceOption, EscalationModalityOption, FeedbackRating, InitChatResponse, LiveAttachment, ProgressStep } from '../api/chatClient';
import { downloadTranscript, type TranscriptEntry } from '../api/transcript';
import { deriveCoarseRegion } from '../api/region';
import { PreChatSurveyCard } from './PreChatSurveyCard';
import { ChatPromptCard, ChatPromptNonAgentModalityLinks } from './ChatPromptCard';
import { AdaptiveCardBubble, SuggestedActionChips, SignInCard, cardSubmitToText } from './AdaptiveCardBubble';
import { botTextToMarkdown, safeHttpUrl } from '../utils/botText';
import { getUserInfo, type UserInfo } from '../api/userClient';
import type { AuthArgs } from '../api/authArgs';
import { ChatTabRail, ChatInfoPanel, type ChatTab } from './ChatInfoPanels';
import { useChatStatus } from './ChatStatus';
import { RuntimeConfigContext } from '../config/runtimeConfigContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { extractSourcesBlock, stripSourcesForDisplay, extractSuggestedPrompts } from '../utils/chatSignals';
import { AgentActivitySteps } from './AgentActivitySteps';
import { LiveAttachmentChips } from './LiveAttachmentChips';
import { initialLivePresentation, legacyLivePresentation, LiveMessageGate, reduceLivePresentation, replaceLiveMessage, restoreLiveHistory, sourceMessageId, useVisibleLiveRead, isTerminalLiveFailure, isUncertainLiveSend, uncertainLiveSendIdentity, reconcileLiveSend, revealLiveSend, useLiveReadReceipts, confirmedReadIdentity, liveMessageScanFeedback } from '../../../../../../partner/widget/src/liveMessages';
import type { LivePresentationState, LiveRecoveryState } from '../../../../../../partner/widget/src/liveMessages';
import { EN_STRINGS } from '../../../../../../partner/widget/src/strings';
import { CaseDetailsMessage, readCaseDetails } from '../../../../../../partner/widget/src/CaseDetailsMessage';
import type { CmspCaseDetails } from '../../../../../../partner/widget/src/contract';
import { markBrowserLiveRead } from '../api/chatClient';

/** Person-with-headset (support agent) glyph — Fluent UI "Person Support". */
const PERSON_SUPPORT_PATH = 'M10.155 14.7734C10.152 14.7662 10.1491 14.7591 10.1461 14.7519C10.0104 14.7147 9.87622 14.6735 9.74386 14.6285C9.74386 14.6285 9.74386 14.6285 9.74386 14.6285C9.74069 14.6274 9.73751 14.6263 9.73433 14.6252C9.26222 14.4638 8.81284 14.2533 8.39229 13.9999C6.35953 12.775 5 10.5463 5 8C5 4.13401 8.13401 1 12 1C15.613 1 18.5867 3.73729 18.9604 7.25118C19.0042 7.66307 18.6642 8 18.25 8C17.8358 8 17.5054 7.66235 17.4496 7.25192C17.0846 4.56822 14.7838 2.5 12 2.5C8.96243 2.5 6.5 4.96243 6.5 8C6.5 10.3965 8.03274 12.435 10.1714 13.1887C10.4826 12.4884 11.1842 12 12 12C13.1045 12 14 12.8954 14 13.9999C14 13.9999 14 13.9999 14 13.9999C14 15.1045 13.1046 16 12 16C11.1696 16 10.4574 15.4939 10.155 14.7734ZM9.12962 16.0027C7.93913 15.5756 6.87142 14.8905 5.99411 14.0146C4.87392 14.1429 4.00391 15.0943 4.00391 16.2488V17.1688C4.00391 17.7408 4.18231 18.2987 4.51427 18.7646C6.05643 20.9289 8.5794 22.0011 12.0004 22.0011C15.4206 22.0011 17.9449 20.9294 19.4908 18.7661C19.8242 18.2996 20.0034 17.7406 20.0034 17.1673V16.2488C20.0034 15.0068 18.9966 13.9999 17.7545 13.9999H15.5C15.5 15.9329 13.933 17.5 12 17.5C10.8102 17.5 9.76137 16.9066 9.12962 16.0027ZM16 8C16 6.76883 15.4438 5.66758 14.5689 4.93383C13.8738 4.35102 12.9776 4 12 4C9.79086 4 8 5.79086 8 8C8 8.60915 8.13627 9.18695 8.38018 9.70425C8.68766 10.3562 9.16608 10.9117 9.75762 11.3128C10.3643 10.8059 11.1458 10.5 12 10.5C12.8533 10.5 13.6352 10.8053 14.2426 11.3127C15.3031 10.5934 16 9.37806 16 8Z';

function SupportPersonIcon({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d={PERSON_SUPPORT_PATH} />
    </svg>
  );
}

/** Circular support-agent avatar (headset person) with an optional status dot. */
function SupportAvatar({ size = 32, dotColor, iconColor = '#0078d4', background = '#e8f0fe' }: { size?: number; dotColor?: string; iconColor?: string; background?: string }) {
  const dot = Math.max(8, Math.round(size * 0.28));
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}>
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SupportPersonIcon size={Math.round(size * 0.62)} color={iconColor} />
      </span>
      {dotColor && (
        <span
          style={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: dot,
            height: dot,
            borderRadius: '50%',
            background: dotColor,
            boxShadow: '0 0 0 2px white',
          }}
        />
      )}
    </span>
  );
}


/** Renders a subset of markdown as React elements: links, bold, inline code, and citation badges. */
function MarkdownText({ text }: { text: string }) {
  const parts = useMemo(() => {
    const normalizedText = text
      .replace(/\\r\\n|\\n|\\r/g, '\n')
      .replace(/\b(?:select|open|choose|click|tap)\s+([^.\n()]{3,80}?)\s*\((ms-windows-store:\/\/[^\s)]+)\)/gi, (_match, label, url) => `Select [${label.trim()}](${url})`);
    const bareUrlPattern = /(?:https?|ms-windows-store):\/\/[^\s\\),\]【】]+/g;
    const result: (string | React.ReactElement)[] = [];
    const normalizeUrl = (url: string) => url.replace(/【\d+:\d+†source】.*$/, '').replace(/[.,;:!?]+$/, '');
    const titleFromUrl = (url: string) => {
      if (/^ms-windows-store:\/\//i.test(url)) {
        return 'Open in Microsoft Store';
      }
      try {
        const parsed = new URL(url);
        const opaquePathSegment = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
        const pathSegment = parsed.pathname
          .split('/')
          .filter(Boolean)
          .pop();
        const slug = pathSegment
          ?.replace(/-[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, '')
          .replace(/[-_]+/g, ' ')
          .trim();
        if (!slug || !pathSegment || opaquePathSegment.test(pathSegment)) return parsed.hostname || url;
        const brandWords: Record<string, string> = {
          microsoft: 'Microsoft',
          teams: 'Teams',
          windows: 'Windows',
          xbox: 'Xbox',
          outlook: 'Outlook',
          onedrive: 'OneDrive',
          office: 'Office',
        };
        const words = slug.split(/\s+/).map((word, index) =>
          brandWords[word.toLowerCase()] ?? (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word),
        );
        return words.join(' ');
      } catch {
        return url;
      }
    };
    const sourceUrls = Array.from(
      new Set(Array.from(normalizedText.matchAll(bareUrlPattern), match => normalizeUrl(match[0]))),
    );
    // Match: citations, markdown links, bold, inline code, bare URLs, and Microsoft Store protocol links.
    const regex = /【(\d+):(\d+)†source】|\[(\d+):(\d+)†source\]|\[([^\]]+)\]\(((?:https?|ms-windows-store):\/\/[^\s\\)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|((?:https?|ms-windows-store):\/\/[^\s\\),\]【】]+)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    let citationCount = 0;
    const citationMap = new Map<string, number>();

    while ((match = regex.exec(normalizedText)) !== null) {
      if (match.index > lastIndex) {
        result.push(normalizedText.slice(lastIndex, match.index));
      }


      if ((match[1] !== undefined && match[2] !== undefined) || (match[3] !== undefined && match[4] !== undefined)) {
        // Citation: 【5:0†source】 or [5:0†source] → numbered source badge.
        const citationTurn = match[1] ?? match[3];
        const citationSource = match[2] ?? match[4];
        const citeKey = `${citationTurn}:${citationSource}`;
        if (!citationMap.has(citeKey)) {
          citationMap.set(citeKey, ++citationCount);
        }
        const num = citationMap.get(citeKey)!;
        const sourceIndex = Number.parseInt(citationSource, 10);
        const previousUrlMatches = Array.from(normalizedText.slice(0, match.index).matchAll(bareUrlPattern));
        const nearestPreviousUrl = previousUrlMatches.length > 0
          ? normalizeUrl(previousUrlMatches[previousUrlMatches.length - 1][0])
          : undefined;
        const href = sourceUrls[sourceIndex]
          ?? sourceUrls[num - 1]
          ?? nearestPreviousUrl
          ?? (sourceUrls.length === 1 ? sourceUrls[0] : undefined);
        const badgeStyle: React.CSSProperties = {
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 16, height: 16, background: '#e8f0fe', color: '#2563eb',
          borderRadius: 999, padding: '0 5px', fontSize: 10, fontWeight: 600,
          marginLeft: 3, marginRight: 1, verticalAlign: 'baseline',
        };
        result.push(
          href ? (
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open source reference ${num}`}
              title={`Open source reference ${num}`}
              style={{ ...badgeStyle, textDecoration: 'none', cursor: 'pointer' }}
            >
              {num}
            </a>
          ) : (
            <span key={key++} title={`Source reference ${num}`} style={{ ...badgeStyle, cursor: 'default' }}>{num}</span>
          )
        );
      } else if (match[5] && match[6]) {
        // Link: [text](url)
        result.push(
          <a key={key++} href={match[6]} target="_blank" rel="noopener noreferrer"
             style={{ color: '#0078d4', textDecoration: 'underline', wordBreak: 'break-all' }}>
            {match[5]}
          </a>
        );
      } else if (match[7]) {
        // Bold: **text**
        result.push(<strong key={key++}>{match[7]}</strong>);
      } else if (match[8]) {
        // Inline code: `text`
        result.push(
          <code key={key++} style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: 3, fontSize: '0.9em' }}>
            {match[8]}
          </code>
        );
      } else if (match[9]) {
        // Bare URL
        const href = normalizeUrl(match[9]);
        result.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer"
             title={href}
             style={{ color: '#0078d4', textDecoration: 'underline', wordBreak: 'break-word' }}>
            {titleFromUrl(href)}
          </a>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < normalizedText.length) {
      result.push(normalizedText.slice(lastIndex));
    }

    return result;
  }, [text]);

  return <>{parts}</>;
}

/** Renders the agent's grounding sources behind a collapsible "Sources" hyperlink. */
function CitationList({ citations }: { citations: Citation[] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, color: '#444', lineHeight: 1.5 }}>
        Learn more about where this information comes from in{' '}
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
          title={expanded ? 'Hide sources' : 'Show sources'}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: '#2563eb', textDecoration: 'underline', font: 'inherit',
          }}
        >
          Sources
        </button>.
      </div>
      {expanded && (
        <ol style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
          {citations.map((c, i) => (
            <li key={i} style={{ marginBottom: 2 }}>
              {c.url ? (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={c.snippet ?? c.url}
                  style={{ color: '#2563eb', textDecoration: 'none', wordBreak: 'break-word' }}
                >
                  {c.title || c.url}
                </a>
              ) : (
                <span title={c.snippet ?? undefined} style={{ color: '#374151' }}>{c.title}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}


interface Message {
  caseDetails?: CmspCaseDetails;
  clientOperationId?: string;
  deliveryUncertain?: boolean;
  pendingSend?: boolean;
  id: string;
  messageId?: string;
  role: 'user' | 'agent' | 'system';
  text: string;
  requiresAuth?: boolean;
  /** Set on a turn where the agent requested human handoff (channelAction=handoff_to_omnichannel). */
  isHandoff?: boolean;
  isTrace?: boolean;
  sentAt?: number;
  receivedAt?: number;
  completedAt?: number;
  senderName?: string;
  isLive?: boolean;
  isBot?: boolean;
  readSequence?: number;
  readOn?: string;
  /** For live-agent messages: semantic category (connecting|queue-position|agent-joined|agent-left|system|agent). */
  liveCategory?: string;
  isHumanAgent?: boolean;
  /** Rich message (activity-aware relay): raw Adaptive Card JSON to render below the text. */
  liveCard?: unknown;
  /** Rich message: OAuth sign-in card from a bot (prompt + sign-in URL). */
  liveAuthCard?: { text: string; url: string; connectionName?: string };
  /** Rich message: suggested-action chips; clicking sends the value as a live message. */
  liveSuggestedActions?: Array<{ title: string; value: unknown; type: string }>;
  liveAttachments?: LiveAttachment[];
  citations?: Citation[];
  suggestedPrompts?: string[];
  /** Server turn id for this agent response — the key feedback is submitted against. */
  turnId?: string;
  /** Local record of the user's thumbs reaction (null until they react). */
  feedback?: FeedbackRating | null;
  /** True while a feedback submit/remove is in flight for this message (buttons disabled). */
  isFeedbackProcessing?: boolean;
  /** True when this agent message is a response to a slash command (no feedback shown). */
  isSlashCommandResponse?: boolean;
  /** True when this turn presented a KB-grounded answer/solution — gates the thumbs feedback bar. */
  feedbackEligible?: boolean;
  availableModalities?: EscalationModalityOption[];
  eligibilityDevices?: EscalationEligibilityDeviceOption[];
  classificationPrompt?: EscalationClassificationPrompt;
  /**
   * Structured input the server asked for on this turn. Rendered by ChatPromptCard and answered by a
   * `promptResponse` turn on the chat stream — no escalation-specific endpoint involved.
   */
  prompt?: ChatPrompt;
  /**
   * Activity narration streamed for this turn ("Investigating your Teams problem"). Present only for
   * agents that authored progress wording.
   */
  progressSteps?: ProgressStep[];
  /** Wall-clock from the send to the first token, shown on the collapsed trace. */
  progressElapsedMs?: number;
  /** Ephemeral progress waypoint shown before reply tokens arrive. */
  statusText?: string;
}

type Mode = 'ai' | 'live-agent';
type LayoutMode = 'float' | 'left' | 'right' | 'fullscreen';

const layoutOptions: { mode: LayoutMode; label: string }[] = [
  { mode: 'float', label: 'Float' },
  { mode: 'left', label: 'Dock left' },
  { mode: 'right', label: 'Dock right' },
  { mode: 'fullscreen', label: 'Full screen' },
];

const DOCKED_CHAT_PAGE_OFFSET_PX = 452;

export type ChatRequest = { action: 'open' } | { action: 'send'; message: string };

export function ChatWidget({ chatRequest, onChatRequestConsumed, entryPointOverride, agentNameOverride }: {
  chatRequest?: ChatRequest | null;
  onChatRequestConsumed?: () => void;
  entryPointOverride?: string;
  /** Pin a specific agent by name (from the agent selector), bypassing server routing rules. */
  agentNameOverride?: string;
}) {
  const status = useChatStatus();
  const cfg = useContext(RuntimeConfigContext);
  const isMobile = useIsMobile();
  const { account, isSignedIn, isAuthPending, signIn, acquireApiToken, puid, tenantId } = useAuth();
  // Seed the open/closed state from the tab session so a mid-conversation refresh reopens the window
  // (and reloads its history) instead of collapsing back to the launcher icon.
  const [open, setOpen] = useState(() => getPersistedChatOpen());
  const [pending, setPending] = useState(false);
  // Seed from the persisted id so a page refresh (F5) reuses the same conversation and the init call
  // can return its history to rehydrate the transcript. Falls back to a fresh id on first ever load.
  const [conversationId, setConversationId] = useState<string | undefined>(() => getPersistedConversationId() ?? undefined);
  useBrowserLiveOwner(conversationId);
  const { observe: observeReceipt, readOn: receiptReadOn } = useLiveReadReceipts(`${account?.homeAccountId}:${conversationId}`);
  const [initialReceiptCapabilities, setInitialReceiptCapabilities] = useState<{
    scope: string; liveCapabilities: StreamEvent['liveCapabilities'];
  } | null>(null);
  useEffect(() => {
    if (initialReceiptCapabilities) observeReceipt(initialReceiptCapabilities, initialReceiptCapabilities.scope);
  }, [initialReceiptCapabilities, conversationId, observeReceipt]);
  const [messages, setMessages] = useState<Message[]>([]);
  const liveMessageGateRef = useRef(new LiveMessageGate());
  const conversationGenerationRef = useRef(0);
  const liveScopeRef = useRef('');
  liveScopeRef.current = `${account?.homeAccountId}:${conversationId}`;
  const [agentIntroduction, setAgentIntroduction] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /**
   * Step tokens whose prompt has been answered. A prompt is not removed from its message (that would
   * strand the customer on a failed turn), so this is what stops an already-answered card becoming
   * clickable again once `pending` clears — otherwise every click returns a replay error.
   */
  const [consumedPromptTokens, setConsumedPromptTokens] = useState<ReadonlySet<string>>(() => new Set());
  /** The step token of the prompt turn currently in flight, so a transport failure can un-consume it. */
  const inFlightPromptTokenRef = useRef<string | null>(null);
  const [lastTrace, setLastTrace] = useState<string | null>(null);
  const [lastEscalationDiagnostics, setLastEscalationDiagnostics] = useState<EscalationDiagnostics | null>(null);
  const [mode, setMode] = useState<Mode>('ai');
  const [recoveryState, setRecoveryState] = useState<LiveRecoveryState>('active');
  const [livePresentation, setLivePresentation] = useState<LivePresentationState>(initialLivePresentation);
  const liveSendBlocked = mode === 'live-agent'
    && (recoveryState !== 'active' || livePresentation.phase === 'connecting' || livePresentation.phase === 'failed');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('float');
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  // Custom float position (top-left px) when the user has dragged the window. null = default anchor.
  const [floatPos, setFloatPos] = useState<{ left: number; top: number } | null>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; originLeft: number; originTop: number } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // Peridot tabbed experience: which section the signed-in user is viewing. 'chat' = the live
  // conversation (default, and the only view anonymous users ever see). Other tabs show read-only
  // context panels (My Info, Subscriptions, History, Product Profile, Device Information).
  const [activeTab, setActiveTab] = useState<ChatTab>('chat');
  const [chatEnded, setChatEnded] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  // Tracks the message whose text was just copied, to briefly swap the copy icon for a check.
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const copiedResetRef = useRef<number | null>(null);
  // Server-side pre-chat survey (Teams handoff + flag): the Adaptive Card to render before going live.
  const [serverPreChatSurvey, setServerPreChatSurvey] = useState<{ survey: unknown; conversationId: string } | null>(null);
  const [submittingServerPreChat, setSubmittingServerPreChat] = useState(false);
  // Server-side post-chat (Customer Voice) survey link surfaced after a live session ends.
  const [serverPostChatSurvey, setServerPostChatSurvey] = useState<{ url: string } | null>(null);
  const [activeStreamMessageId, setActiveStreamMessageId] = useState<string | null>(null);
  const [initChatDone, setInitChatDone] = useState(false);
  // Set when chat/init reports the resumed conversation is still in a live Omnichannel handoff. Consumed
  // by an effect below rather than acted on inline, because enterServerLiveMode is declared later in this
  // component (referencing it from callInitChat's dependency array would hit the temporal dead zone).
  const [pendingLiveResume, setPendingLiveResume] =
    useState<{
      sessionId: string;
      conversationId: string;
      presentation?: LivePresentationState;
    } | null>(null);
  const [reconnectPending, setReconnectPending] = useState(() => readReconnectIdFromUrl() !== null);
  const reconnectStartedRef = useRef(false);
  // When set, the negative-feedback reason dialog is open for this message/turn.
  const [reasonDialog, setReasonDialog] = useState<{ messageId: string; turnId: string } | null>(null);
  // Server-side live-agent (Omnichannel bridge) session: id + the poll-loop controller.
  const liveSessionIdRef = useRef<string | null>(null);
  const liveConversationIdRef = useRef<string | null>(null);
  const liveEventsAbortRef = useRef<AbortController | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const markRead = useCallback(async (id: string) => {
    if (conversationId) await markBrowserLiveRead(conversationId, id);
  }, [conversationId]);
  useVisibleLiveRead(scrollRef, messages, `${account?.homeAccountId}:${conversationId}`,
    open && !minimized && activeTab === 'chat' && mode === 'live-agent' && !chatEnded && !liveSendBlocked && !isAuthPending, markRead);
  const originalBodyPaddingRef = useRef<{ left: string; right: string } | null>(null);
  const messagesRef = useRef<Message[]>([]);
  // Counts consecutive thumbs-down within the current Q&A thread. First two downs regenerate the
  // answer; the third opens the reason dialog. Reset on a new user question or a thumbs-up.
  const downCountRef = useRef(0);
  const [liveAgentName, setLiveAgentName] = useState<string | null>(null);
  const [agentSeen, setAgentSeen] = useState(false);
  const sentCountRef = useRef<number | null>(0);
  const initChatPromiseRef = useRef<Promise<string | undefined> | null>(null);
  // Bounds automatic re-sends of a live message that hit the OC warm-up window (retryMessage), so a
  // persistently-warming session can't loop forever. Reset when a message is actually delivered.
  const liveResendCountRef = useRef(0);
  // The signed-in user's PUID/tenant, captured as soon as they're known (sign-in) so a later
  // chat-session init includes them even if the user signed in BEFORE opening the chat. Reading
  // from account.idTokenClaims already works, but the captured copy survives any transient
  // account-claim gaps and decouples "when we learned the identity" from "when we init the session".
  const capturedIdentityRef = useRef<{ puid?: string; tenantId?: string }>({});
  useEffect(() => {
    if (puid || tenantId) {
      capturedIdentityRef.current = {
        puid: puid ?? capturedIdentityRef.current.puid,
        tenantId: tenantId ?? capturedIdentityRef.current.tenantId,
      };
    }
  }, [puid, tenantId]);
  // Reset conversation when the agent selector changes the entry point OR the pinned agent. Both matter:
  // several selector options share an entry point (e.g. the KA and unauthenticated sign-in helpers both
  // use "uat-ka"), so keying off the entry point alone would miss a switch between two same-entry agents.
  const prevAgentSelectionRef = useRef(`${entryPointOverride ?? ''}|${agentNameOverride ?? ''}`);
  useEffect(() => {
    const selectionKey = `${entryPointOverride ?? ''}|${agentNameOverride ?? ''}`;
    if (prevAgentSelectionRef.current !== selectionKey) {
      prevAgentSelectionRef.current = selectionKey;
      abortRef.current?.abort();
      liveEventsAbortRef.current?.abort();
      liveEventsAbortRef.current = null;
      liveSessionIdRef.current = null;
      liveConversationIdRef.current = null;
      clearPersistedConversationId();
      setConversationId(undefined);
      liveMessageGateRef.current.reset();
      conversationGenerationRef.current += 1;
      initChatPromiseRef.current = null;
      messagesRef.current = [];
      setMessages([]);
      setAgentIntroduction(null);
      setDraft('');
      setInitChatDone(false);
      setChatEnded(false);
      setMode('ai');
      setRecoveryState('active');
      setLivePresentation(initialLivePresentation);
      setLiveAgentName(null);
      setAgentSeen(false);
      sentCountRef.current = 0;
      setLastTrace(null);
      setLastEscalationDiagnostics(null);
    }
  }, [entryPointOverride, agentNameOverride]);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const focusComposer = useCallback(() => {
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const leaveServerLiveMode = useCallback(() => {
    liveEventsAbortRef.current?.abort();
    liveEventsAbortRef.current = null;
    liveSessionIdRef.current = null;
    liveConversationIdRef.current = null;
    setPendingLiveResume(null);
    setServerPreChatSurvey(null);
    setLiveAgentName(null);
    setAgentSeen(false);
    sentCountRef.current = 0;
    setMode('ai');
    setRecoveryState('active');
  }, []);

  // Calls POST /api/v1/chat/init to cache identity before streaming.
  const callInitChat = useCallback(async (): Promise<string | undefined> => {
    if (initChatPromiseRef.current) {
      return initChatPromiseRef.current;
    }

    const initialize = async (): Promise<string | undefined> => {
      const generation = conversationGenerationRef.current;
      try {
      // Bearer is optional for own-SPA requests (middleware trusts same-origin).
      const bearer = await acquireApiToken();
      if (generation !== conversationGenerationRef.current) return undefined;

      // Resume the persisted conversation if there is one; otherwise init issues the id. The widget never makes one up.
      const convId = conversationId ?? getPersistedConversationId() ?? undefined;
      const response = await initChat(
        {
          conversationId: convId,
          sourceAppName: 'CMSP Portal',
          callerAppId: cfg?.clientId ?? 'unknown',
          locale: navigator.language || 'en-US',
          region: deriveCoarseRegion(),
          isAuthenticated: isSignedIn,
          homeAccountId: account?.homeAccountId,
          puid: puid ?? capturedIdentityRef.current.puid,
          tenantId: tenantId ?? capturedIdentityRef.current.tenantId,
          email: account?.username,
          displayName: account?.name,
          entryPoint: entryPointOverride ?? detectEntryPoint(),
          agentName: agentNameOverride,
        },
        bearer,
        {},
      );
      if (generation !== conversationGenerationRef.current) return undefined;

      setInitialReceiptCapabilities({
        scope: `${account?.homeAccountId}:${response.conversationId}`,
        liveCapabilities: response.liveCapabilities,
      });
      setConversationId(response.conversationId);
      // Init may be followed by a same-tick bridge message before React renders.
      // Publish the validated scope now; the generation check above rejects stale init.
      liveScopeRef.current = `${account?.homeAccountId}:${response.conversationId}`;
      // Persist so the next page refresh reuses this conversation and can rehydrate its history.
      setPersistedConversationId(response.conversationId);
      setInitChatDone(true);
      setAgentIntroduction(response.agentintroduction?.trim() || null);

      // A server cache miss can still restore an existing browser-owned provider conversation.
      // Rehydrate the transcript from them — but only when the widget hasn't already accumulated
      // messages this session, so we never duplicate or clobber a live in-memory conversation.
      const restoredBrowserHistory = isBrowserLiveConversation(response.conversationId) && !!response.history?.length;
      let restoredLivePresentation: LivePresentationState | undefined;
      if ((!response.isNew || restoredBrowserHistory) && response.history && response.history.length > 0 && messagesRef.current.length === 0) {
          const { messages: history, agentName } = restoreLiveHistory(liveMessageGateRef.current, response.history);
          restoredLivePresentation = response.history.reduce((current, row) => reduceLivePresentation(current, {
            messageId: row.messageId,
            senderName: row.senderName,
            senderKind: row.isLiveAgent ? 'human' : row.senderKind === 'bot' ? 'bot' : 'system',
            liveCategory: row.liveCategory,
            recoveryState: row.liveCategory === 'connecting' ? 'recovering'
              : row.liveCategory === 'waiting' ? 'active' : undefined,
          }), initialLivePresentation);
          const restored = history.map(h => {
            const identity = { id: sourceMessageId(h.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(h.messageId) };
            if (h.role === 'user') {
              return { ...identity, ...confirmedReadIdentity(h), role: 'user' as const, text: h.text };
            }
            if (h.role === 'system') {
              return { ...identity, role: 'system' as const, text: h.text, liveCategory: h.liveCategory ?? undefined };
            }
            // A human live-agent turn is plain prose from a person: none of the AI cleanup below applies
            // (its text is not model output), and it must keep the "Live agent" attribution it had live.
            if (h.isLiveAgent || h.senderKind === 'bot') {
              return {
                ...identity,
                role: 'agent' as const,
                text: botTextToMarkdown(h.bodyText ?? h.text),
                isLive: h.isLiveAgent === true,
                isBot: h.senderKind === 'bot',
                liveAttachments: h.attachments,
                liveCategory: 'agent' as const,
                isHumanAgent: h.isLiveAgent === true,
                senderName: h.senderName ?? undefined,
              };
            }
            // Agent message: restore the thumbs feedback bar, any prior rating, and the sources link.
            // completedAt is set so the bar's render gate (feedbackEligible && turnId && completedAt)
            // passes on the resumed turn. The stored reply is the raw agent text, so apply the same
            // sources-block + suggested-prompt cleanup the live "done" handler does.
            let text = h.text;
            let citations: Citation[] | undefined =
              h.citations && h.citations.length > 0 ? h.citations : undefined;
            const sourcesResult = extractSourcesBlock(text);
            if (sourcesResult.sources.length > 0) {
              text = sourcesResult.text;
              if (!citations) {
                citations = sourcesResult.sources;
              }
            }
            if (citations && citations.length > 0) {
              text = text.replace(/\r\n/g, '\n').replace(/\n*\s*Sources\s*\n(\s*\[\d+\][^\n]*\n*)+\s*$/i, '').trimEnd();
            }
            const extracted = extractSuggestedPrompts(text);
            return {
              ...identity,
              role: 'agent' as const,
              text: extracted.text,
              caseDetails: readCaseDetails(h.caseDetails),
              suggestedPrompts: extracted.suggestedPrompts.length > 0 ? extracted.suggestedPrompts : undefined,
              citations,
              turnId: h.turnId ?? undefined,
              feedbackEligible: h.feedbackEligible ?? undefined,
              feedback: h.feedback ?? null,
              completedAt: h.feedbackEligible && h.turnId ? Date.now() : undefined,
            };
          });
        messagesRef.current = restored;
        setMessages(prev => prev.length > 0 ? prev : restored);

        setLiveAgentName(agentName);
        if (restoredLivePresentation) setLivePresentation(restoredLivePresentation);
      }

      // Show greeting only for new sessions.
      if (response.isNew && response.greeting && !restoredBrowserHistory) {
        setMessages(prev => [
          ...prev,
          { id: crypto.randomUUID(), role: 'agent' as const, text: response.greeting! },
        ]);
      }
      // The refresh landed mid-live-chat: the conversation is still handed off to a human agent and the
      // server handed back the bridge session to resume. Without this the widget would sit in AI mode
      // while the agent waits on the other side, and the customer's next message would go to the bot.
      if (response.mode === 'live' && response.sessionId) {
        setPendingLiveResume({
          sessionId: response.sessionId,
          conversationId: response.conversationId,
          presentation: restoredLivePresentation,
        });
      } else if (response.mode === 'ended') {
        // The agent closed the chat while the browser was away, so no poll turn ever surfaced the 'ended'
        // frame. Show the same terminal state the live stream would have, rather than handing back a
        // working AI composer with no explanation for where the human went.
        //
        // Guarded because this can run more than once for the same conversation: callInitChat re-runs on
        // the anonymous -> signed-in transition, and unlike the history branch above (which REPLACES the
        // transcript) this one appends. Without the guard the customer sees the same sentence twice.
        setMessages(prev =>
          prev.some(m => m.liveCategory === 'agent-left' || m.liveCategory === 'ended')
            ? prev
            : [...prev, {
                id: crypto.randomUUID(), role: 'system' as const,
                text: 'The live agent has ended the chat.', liveCategory: 'agent-left',
              }],
        );
        setChatEnded(true);
      }
      return response.conversationId;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[chatWidget] initChat failed:', err);
      return undefined;
    }
    };

    const promise = initialize();
    initChatPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      if (initChatPromiseRef.current === promise) {
        initChatPromiseRef.current = null;
      }
    }
  }, [acquireApiToken, account, cfg, conversationId, isSignedIn, puid, tenantId, entryPointOverride, agentNameOverride]);

  useEffect(() => {
    // Reconnect also stamps chat identity via initChat, so wait for MSAL to restore cached accounts.
    if (!reconnectPending || reconnectStartedRef.current || isAuthPending) return;
    const reconnectId = readReconnectIdFromUrl();
    if (!reconnectId) {
      setReconnectPending(false);
      return;
    }
    reconnectStartedRef.current = true;

    void (async () => {
      let navigatingAway = false;
      try {
        const bearer = await acquireApiToken().catch(() => null);
        const entryPoint = entryPointOverride ?? detectEntryPoint();
        const locale = navigator.language || 'en-US';
        const region = deriveCoarseRegion();
        const availability = await checkReconnectAvailability(
          { reconnectId, entryPoint, locale, region },
          bearer,
        );

        if (!availability.available) {
          if (availability.redirectUrl) {
            navigatingAway = true;
            window.location.replace(availability.redirectUrl);
            return;
          }
          setReconnectPending(false);
          return;
        }

        const response = await initChat(
          {
            sourceAppName: 'CMSP Portal',
            callerAppId: cfg?.clientId ?? 'unknown',
            locale,
            region,
            isAuthenticated: isSignedIn,
            homeAccountId: account?.homeAccountId,
            puid: puid ?? capturedIdentityRef.current.puid,
            tenantId: tenantId ?? capturedIdentityRef.current.tenantId,
            email: account?.username,
            displayName: account?.name,
            entryPoint,
            action: 'escalation',
            reconnectId,
          },
          bearer,
        );

        setConversationId(response.conversationId);
        setPersistedConversationId(response.conversationId);
        setInitChatDone(true);
        setAgentIntroduction(response.agentintroduction?.trim() || null);
        setOpen(true);

        if (response.greeting) {
          setMessages(prev => [
            ...prev,
            { id: crypto.randomUUID(), role: 'agent' as const, text: response.greeting!, isLive: true, liveCategory: 'agent' as const },
          ]);
        }
        if (response.mode === 'live') {
          setPendingLiveResume({
            sessionId: response.sessionId ?? response.conversationId,
            conversationId: response.conversationId,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[chatWidget] reconnect link redemption failed; starting a normal chat:', err);
      } finally {
        if (!navigatingAway) {
          stripReconnectIdFromUrl();
          setReconnectPending(false);
        }
      }
    })();
  }, [reconnectPending, acquireApiToken, entryPointOverride, cfg, isSignedIn, account, puid, tenantId, isAuthPending]);

  // Calls POST /api/v1/chat/init with action:"escalation" the moment the AI hands off to a human agent
  // (this turn's done event carries mode==="live") — the SAME HTTP entry point (and IHumanEscalationService
  // code) a BYOA/static caller uses to connect directly to a human agent with no AI turn at all. This makes
  // every escalation trigger, AI-driven or not, go through one real, observable chat/init call instead of
  // relying purely on the AI turn's own server-side state marking. Passes the conversation transcript so
  // far as `summary`, matching what BYOA sends. Returns null on failure so the caller can fall back to the
  // existing poll-driven flow (which still starts the session on its own if this call didn't get there first).
  const callEscalationInit = useCallback(async (
    liveConversationId: string,
    transcript: Message[],
  ): Promise<InitChatResponse | null> => {
    // NOTE: do NOT early-return when the conversation is already browser-live-bound. The shared
    // Omnichannel coordinator binds the conversation synchronously (observe() on the
    // handoff_to_omnichannel done event) BEFORE this runs, so isBrowserLiveConversation() is already
    // true at the initial handoff. Skipping here would drop the action:"escalation" init that
    // establishes the server-side human-handoff binding (State=Handoff + ActiveAgentName), so the
    // subsequent browser-SDK /omnichannel/bootstrap fails with human_handoff_required. The init is
    // idempotent — on an already-established handoff it returns the existing binding — so running it at
    // the handoff transition is safe.
    if (!liveConversationId) return null;
    try {
      const bearer = await acquireApiToken().catch(() => null);
      const summary = JSON.stringify(transcript.filter(m => !m.pendingSend).map(m => ({
        role: m.role,
        text: m.text,
        sender: m.senderName ?? (m.role === 'agent' ? 'Bot' : m.role === 'user' ? 'You' : 'System'),
        at: new Date(m.receivedAt ?? m.sentAt ?? Date.now()).toISOString(),
      })));

      return await initChat(
        {
          conversationId: liveConversationId,
          sourceAppName: 'CMSP Portal',
          callerAppId: cfg?.clientId ?? 'unknown',
          locale: navigator.language || 'en-US',
          region: deriveCoarseRegion(),
          isAuthenticated: isSignedIn,
          homeAccountId: account?.homeAccountId,
          puid: puid ?? capturedIdentityRef.current.puid,
          tenantId: tenantId ?? capturedIdentityRef.current.tenantId,
          email: account?.username,
          displayName: account?.name,
          entryPoint: entryPointOverride ?? detectEntryPoint(),
          summary,
          action: 'escalation',
        },
        bearer,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[chatWidget] escalation initChat failed; falling back to the poll-driven handoff:', err);
      return null;
    }
  }, [acquireApiToken, cfg, isSignedIn, account, puid, tenantId, entryPointOverride]);

  // User profile fetched from the backend after sign-in. The backend resolves identity and
  // enriches it from Microsoft Graph /me via an On-Behalf-Of exchange (mirrors the Emerald flow).
  const [userInfoState, setUserInfoState] = useState<{ homeAccountId: string; info: UserInfo } | null>(null);
  // Hide another account's profile during render, before the effect can clear the stored result.
  const userInfo = isSignedIn && userInfoState?.homeAccountId === account?.homeAccountId
    ? userInfoState?.info ?? null
    : null;
  const profileSessionActive = open || initChatDone;

  // Defer profile/token acquisition until chat is active; each effect owns its request so an old
  // account's pending token or HTTP call cannot suppress or overwrite the next account's profile.
  useEffect(() => {
    setUserInfoState(null);
    if (!isSignedIn || !account?.homeAccountId || !profileSessionActive) return;
    const controller = new AbortController();
    const homeAccountId = account.homeAccountId;
    void (async () => {
      try {
        const bearer = await acquireApiToken();
        if (controller.signal.aborted) return;
        if (!bearer) throw new Error('No authentication available for user profile.');
        const info = await getUserInfo({
          authBearer: bearer,
          homeAccountId,
          username: account.username,
        }, controller.signal);
        if (!controller.signal.aborted) setUserInfoState({ homeAccountId, info });
      } catch (err) {
        if (!controller.signal.aborted) {
          // eslint-disable-next-line no-console
          console.warn('[chatWidget] user info fetch failed:', err);
        }
      }
    })();
    return () => controller.abort();
  }, [acquireApiToken, account, isSignedIn, profileSessionActive]);

  // Auth args for the context panels' delegated reads (subscriptions/products/cases):
  // build the same MSAL-bearer + identity-hint shape the API clients expect. Returns null when the
  // user isn't signed in or a token can't be acquired, so panels fall back to their empty state.
  const getPanelAuthArgs = useCallback(async (): Promise<AuthArgs | null> => {
    if (!isSignedIn || !account?.homeAccountId) return null;
    const bearer = await acquireApiToken();
    if (!bearer) return null;
    return {
      authBearer: bearer,
      homeAccountId: account.homeAccountId,
      username: account.username,
      displayName: account.name,
    };
  }, [isSignedIn, account, acquireApiToken]);

  // Tabs are a signed-in-only surface. On sign-out, snap back to the plain chat view so the next
  // (anonymous) user never sees a stale context tab or another user's data.
  useEffect(() => {
    if (!isSignedIn) setActiveTab('chat');
  }, [isSignedIn]);

  // Call initChat when the chat window opens.
  //
  // Waits for MSAL to settle (isAuthPending) before the FIRST init. On a page load with a cached
  // session, MSAL briefly reports no account, so an init fired here would stamp the conversation
  // anonymous — permanently, because the server caches session identity once per conversation. The
  // signed-in upgrade below cannot repair it either: it only fires on a false->true transition
  // within the page's lifetime, and a cached restore never produces that transition.
  useEffect(() => {
    if (open && !initChatDone && !reconnectPending && !isAuthPending) {
      void callInitChat();
    }
  }, [open, initChatDone, reconnectPending, isAuthPending, callInitChat]);

  // Persist the open/closed state (tab-scoped) so a refresh restores the window instead of collapsing to
  // the launcher icon. Paired with the seeded initial state above.
  useEffect(() => {
    setPersistedChatOpen(open);
  }, [open]);

  const wasSignedInRef = useRef<boolean | undefined>(isAuthPending ? undefined : isSignedIn);
  useEffect(() => {
    const styleId = 'cmsp-chat-activity-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = '@keyframes cmsp-chat-pulse{0%,80%,100%{opacity:.35;transform:scale(.75)}40%{opacity:1;transform:scale(1)}}';
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (isAuthPending) return;
    if (wasSignedInRef.current === undefined) {
      wasSignedInRef.current = isSignedIn;
      return;
    }
    if (!wasSignedInRef.current && isSignedIn) {
      // Mid-conversation sign-in: only upgrade the cached identity if a chat session is actually
      // active (window open or an init already happened). If the user signed in BEFORE starting the
      // chat, we do NOT fire a premature init — the PUID is already captured (see capturedIdentityRef)
      // and the first init, when the window opens, will include it.
      const chatSessionActive = open || initChatDone;
      if (chatSessionActive && !reconnectPending) {
        void callInitChat();
        setMessages(prev => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'agent',
            text: `Signed in as ${account?.username ?? 'you'}. I can now help with subscription and account actions.`,
          },
        ]);
      }
    }
    wasSignedInRef.current = isSignedIn;
  }, [isSignedIn, isAuthPending, account, callInitChat, open, initChatDone, reconnectPending]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: activeStreamMessageId ? 'auto' : 'smooth',
    });
  }, [messages, open, activeStreamMessageId]);

  useEffect(() => {
    if (!open) return;
    focusComposer();
  }, [focusComposer, open]);

  useEffect(() => {
    if (!open || pending) return;
    focusComposer();
  }, [focusComposer, mode, open, pending]);

  useEffect(() => {
    const isDocked = open && !isMobile && !minimized && (layoutMode === 'left' || layoutMode === 'right');
    const body = document.body;

    if (!isDocked) {
      if (originalBodyPaddingRef.current) {
        body.style.paddingLeft = originalBodyPaddingRef.current.left;
        body.style.paddingRight = originalBodyPaddingRef.current.right;
        originalBodyPaddingRef.current = null;
      }
      return;
    }

    if (!originalBodyPaddingRef.current) {
      originalBodyPaddingRef.current = {
        left: body.style.paddingLeft,
        right: body.style.paddingRight,
      };
    }

    const offset = `${DOCKED_CHAT_PAGE_OFFSET_PX}px`;
    body.style.paddingLeft = layoutMode === 'left' ? offset : originalBodyPaddingRef.current.left;
    body.style.paddingRight = layoutMode === 'right' ? offset : originalBodyPaddingRef.current.right;

    return () => {
      if (!originalBodyPaddingRef.current) return;
      body.style.paddingLeft = originalBodyPaddingRef.current.left;
      body.style.paddingRight = originalBodyPaddingRef.current.right;
      originalBodyPaddingRef.current = null;
    };
  }, [layoutMode, open, isMobile, minimized]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = 'auto';
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 128)}px`;
  }, [draft]);

  const getAttachmentAuth = useCallback(async () => {
    const bearer = isSignedIn ? await acquireApiToken().catch(() => null) : null;
    return {
      authBearer: bearer ?? undefined,
      homeAccountId: account?.homeAccountId,
      username: account?.username,
    };
  }, [isSignedIn, acquireApiToken, account]);

  // Fully server-side handoff: the ORCHESTRATOR starts and owns the Omnichannel session (on the AI
  // turn's done event). The widget only SUBSCRIBES to the session's read-only event stream and renders
  // events by category — it never starts, binds, sends-to, or (except on New Chat) ends the session.
  // ── Live-agent (server-side, SSE-free) ──────────────────────────────────────────────────────────
  // The human agent is served as an IAgentClient; live frames arrive as mode="live" tokens on
  // /chat/stream. Render a human message vs a system/lifecycle frame by senderKind.
  const appendLiveToken = useCallback((evt: StreamEvent, receiptScope: string) => {
    if (receiptScope !== liveScopeRef.current) return;
    const scanFeedback = liveMessageScanFeedback(evt, EN_STRINGS);
    if (scanFeedback) {
      if (evt.clientOperationId) {
        if (!liveMessageGateRef.current.remember(`scan:${evt.clientOperationId}`)) return;
        const unsent = messagesRef.current.find(message =>
          message.clientOperationId === evt.clientOperationId && message.role === 'user');
        setMessages(previous => previous.filter(message => message.clientOperationId !== evt.clientOperationId));
        if (unsent) setDraft(current => current || unsent.text);
      }
      setMessages(previous => [...previous, { id: crypto.randomUUID(), role: 'system', text: scanFeedback }]);
      return;
    }
    if (isUncertainLiveSend(evt) && evt.clientOperationId) {
      setMessages(previous => previous.map(message => message.clientOperationId === evt.clientOperationId
        ? { ...message, ...uncertainLiveSendIdentity(evt, message) } : message));
      return;
    }
    if (evt.senderKind === 'system' && evt.liveCategory === 'message-sent') {
      setMessages(previous => reconcileLiveSend(previous, evt));
    }
    if (evt.recoveryState) setRecoveryState(evt.recoveryState);
    setLivePresentation(previous => reduceLivePresentation(previous, evt));
    if (evt.senderKind === 'system' && ['capabilities', 'read-receipt'].includes(evt.liveCategory ?? '')) {
      observeReceipt(evt, receiptScope);
      return;
    }
    if (evt.messageUpdated) {
      const patch = {
        text: evt.text ? botTextToMarkdown(evt.text) : '',
        senderName: evt.senderName?.trim() || undefined,
        liveAttachments: evt.attachments?.length ? evt.attachments : undefined,
      };
      messagesRef.current = replaceLiveMessage(messagesRef.current, evt.messageId, patch);
      setMessages(previous => replaceLiveMessage(previous, evt.messageId, patch));
      return;
    }
    const accepted = liveMessageGateRef.current.accept(evt);
    if (!accepted) return;
    const attachments = evt.attachments ?? [];
    const sender = evt.senderName?.trim();
    if (accepted.agentName !== undefined) setLiveAgentName(accepted.agentName);
    if (evt.senderKind !== 'bot' && evt.liveCategory === 'message-read'
      && (!conversationId || !isBrowserLiveConversation(conversationId))) {
      if (evt.readSequence != null && evt.readSequence === sentCountRef.current) {
        setAgentSeen(true);
      }
      return;
    }
    if (evt.senderKind !== 'bot' && evt.liveCategory === 'message-sent') {
      if (evt.readSequence != null) sentCountRef.current = evt.readSequence;
      return;
    }
    if (!accepted.show || (!evt.text && attachments.length === 0)) return;
    if (evt.senderKind === 'human' || evt.senderKind === 'bot') {
      const message: Message = {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(),
        messageId: sourceMessageId(evt.messageId),
        role: 'agent',
        text: evt.text ? botTextToMarkdown(evt.text) : '',
        isLive: evt.senderKind === 'human',
        isBot: evt.senderKind === 'bot',
        liveCategory: 'agent',
        isHumanAgent: evt.senderKind === 'human',
        senderName: sender || undefined,
        liveAttachments: attachments.length > 0 ? attachments : undefined,
      };
      messagesRef.current = [...messagesRef.current, message];
      setMessages(prev => [...prev, message]);
    } else {
      if (!evt.text) return;
      const message: Message = {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(evt.messageId),
        role: 'system', text: evt.text!, liveCategory: evt.liveCategory ?? 'system',
      };
      messagesRef.current = [...messagesRef.current, message];
      setMessages(prev => [...prev, message]);
    }
  }, [observeReceipt, conversationId]);

  const handleLiveEnded = useCallback((evt: StreamEvent, terminal: boolean) => {
    // Replay protection applies to the notice, never to terminal session metadata.
    const accepted = liveMessageGateRef.current.accept({ ...evt, liveCategory: 'ended' });
    if (accepted?.show) {
      setMessages(prev => [...prev, {
        id: sourceMessageId(evt.messageId) ?? crypto.randomUUID(), messageId: sourceMessageId(evt.messageId), role: 'system',
        text: evt.text || 'The live agent has ended the conversation.', liveCategory: 'agent-left',
      }]);
    }
    const surveyUrl = safeHttpUrl(evt.postChatSurveyUrl);
    if (surveyUrl) setServerPostChatSurvey({ url: surveyUrl });
    setMode('ai');
    setLiveAgentName(null);
    setAgentSeen(false);
    sentCountRef.current = 0;
    liveSessionIdRef.current = null;
    liveEventsAbortRef.current?.abort();
    liveEventsAbortRef.current = null;
    // Terminal (human agent ended): the whole conversation is over — disable the composer, leaving only
    // "New Chat". Transient ends (session-gone) keep the composer so the customer can resend to the AI.
    if (terminal) setChatEnded(true);
  }, []);

  // One "poll turn": an empty /chat/stream request the server treats as a drain of the live thread,
  // returning any new agent/lifecycle frames as mode="live" tokens. Resolves with the outcome: the session
  // ended, a pre-chat survey must be shown before the session starts, or keep polling.
  const runLivePollTurn = useCallback((liveConversationId: string, signal: AbortSignal) =>
    new Promise<'ended' | 'survey' | 'continue' | 'failed'>((resolve) => {
      void (isSignedIn ? acquireApiToken().catch(() => null) : Promise.resolve(null)).then((bearer) => {
        if (signal.aborted) { resolve('continue'); return; }
        let outcome: 'ended' | 'survey' | 'continue' = 'continue';
        const ctrl = sendChatMessageStream(
          {
            message: '', conversationId: liveConversationId, authBearer: bearer ?? undefined, poll: true,
            homeAccountId: account?.homeAccountId, username: account?.username, displayName: account?.name,
          },
          {
            onLiveToken: (e: StreamEvent) => {
              if (!signal.aborted) appendLiveToken(e, `${account?.homeAccountId}:${liveConversationId}`);
              if (isTerminalLiveFailure(e)) { ctrl.abort(); resolve('failed'); }
            },
            onDone: (e: StreamEvent) => {
              if (signal.aborted) { resolve('continue'); return; }
              if (e.escalationDiagnostics) setLastEscalationDiagnostics(e.escalationDiagnostics);
              if (e.mode === 'ended') { outcome = 'ended'; handleLiveEnded(e, true); }
              else if (e.mode === 'ai') { outcome = 'ended'; handleLiveEnded(e, false); }
              else if (e.mode === 'prechat-survey' && e.preChatSurvey) {
                outcome = 'survey';
                setServerPreChatSurvey({ survey: e.preChatSurvey, conversationId: liveConversationId });
              }
              resolve(outcome);
            },
            onError: (error) => {
              if (!signal.aborted && isBrowserLiveConversation(liveConversationId)) {
                if (isTerminalLiveFailure(error)) {
                  setRecoveryState('failed');
                  setLivePresentation(previous => reduceLivePresentation(previous, {
                    liveCategory: 'error', recoveryState: 'failed',
                  }));
                }
                setMessages(prev => prev.at(-1)?.text === error.message ? prev : [
                  ...prev, { id: crypto.randomUUID(), role: 'system', text: error.message },
                ]);
              }
              resolve(isTerminalLiveFailure(error) ? 'failed' : 'continue');
            },
          },
        );
        signal.addEventListener('abort', () => ctrl.abort(), { once: true });
      });
    }), [isSignedIn, acquireApiToken, account, appendLiveToken, handleLiveEnded]);

  // Fully server-side handoff: the human-agent client owns the OC session. With the SSE retired, the widget
  // surfaces the agent's asynchronous replies by long-polling /chat/stream (empty poll turns) until the
  // session ends / New Chat / unmount.
  const enterServerLiveMode = useCallback((
    sessionId: string,
    liveConversationId: string,
    restoredPresentation?: LivePresentationState,
  ) => {
    if (liveSessionIdRef.current === sessionId && liveEventsAbortRef.current) {
      return; // already live on this session
    }
    liveEventsAbortRef.current?.abort();
    liveSessionIdRef.current = sessionId;
    liveConversationIdRef.current = liveConversationId;
    setServerPreChatSurvey(null);
    const browserLive = isBrowserLiveConversation(liveConversationId);
    setLivePresentation(browserLive ? restoredPresentation ?? initialLivePresentation : legacyLivePresentation);
    setRecoveryState(browserLive && (!restoredPresentation || restoredPresentation.phase === 'connecting')
      ? 'recovering'
      : restoredPresentation?.phase === 'failed' ? 'failed' : 'active');
    setMode('live-agent');

    const loop = new AbortController();
    liveEventsAbortRef.current = loop;
    void (async () => {
      while (!loop.signal.aborted && liveSessionIdRef.current === sessionId) {
        const result = await runLivePollTurn(liveConversationId, loop.signal);
        if (result === 'ended' || result === 'failed' || loop.signal.aborted) break;
        if (result === 'survey') {
          // A pre-chat survey must be answered before the (re)start. Tear down THIS poll loop and clear
          // the session binding so submitting the survey starts a FRESH loop — even if the server returns
          // the same sessionId (otherwise the "already live on this session" guard above early-returns and
          // no new poll loop starts, so the agent's replies are never polled and the chat appears frozen).
          if (liveEventsAbortRef.current === loop) liveEventsAbortRef.current = null;
          if (liveSessionIdRef.current === sessionId) liveSessionIdRef.current = null;
          loop.abort();
          break;
        }
        await new Promise<void>((r) => {
          const t = setTimeout(r, 3000);
          loop.signal.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
        });
      }
    })();
  }, [runLivePollTurn]);

  // Re-enter live mode for a conversation resumed mid-handoff (see pendingLiveResume). Runs here rather
  // than inline in callInitChat so it can reference enterServerLiveMode, which is declared above this
  // point. enterServerLiveMode is idempotent, and the flag is cleared so a re-render can't restart it.
  useEffect(() => {
    if (!pendingLiveResume) return;
    enterServerLiveMode(
      pendingLiveResume.sessionId,
      pendingLiveResume.conversationId,
      pendingLiveResume.presentation,
    );
    setPendingLiveResume(null);
  }, [pendingLiveResume, enterServerLiveMode]);

  // Submit the pre-chat answers as a normal /chat/stream turn — the human-agent client uses them to start
  // the server-owned session, then the widget resumes live polling. The SPA only ever talks to the API via
  // init + stream.
  const submitServerPreChatSurvey = useCallback(async (response: Record<string, unknown>) => {
    const pending = serverPreChatSurvey;
    if (!pending || submittingServerPreChat) return;
    setSubmittingServerPreChat(true);
    setServerPreChatSurvey(null);
    try {
      const authBearer = await acquireApiToken().catch(() => null);
      await new Promise<void>((resolve, reject) => {
        sendChatMessageStream(
          {
            message: JSON.stringify(response), conversationId: pending.conversationId, authBearer: authBearer ?? undefined,
            homeAccountId: account?.homeAccountId, username: account?.username, displayName: account?.name,
          },
          {
            onLiveToken: (e: StreamEvent) => appendLiveToken(e, `${account?.homeAccountId}:${pending.conversationId}`),
            onDone: (e: StreamEvent) => {
              if (e.escalationDiagnostics) setLastEscalationDiagnostics(e.escalationDiagnostics);
              if (e.mode === 'ended') { handleLiveEnded(e, true); }
              else if (e.mode === 'ai') { handleLiveEnded(e, false); }
              else { enterServerLiveMode(e.sessionId ?? pending.conversationId, pending.conversationId); }
              resolve();
            },
            onError: (err: Error) => reject(err),
          },
        );
      });
    } catch (err) {
      if (err instanceof ChatStreamError && err.code === 'live_agent_unavailable') {
        setServerPreChatSurvey(pending);
      }
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'system',
        text: 'Sorry, we could not connect you to a live agent. Please try again.',
        liveCategory: 'system',
      }]);
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSubmittingServerPreChat(false);
    }
  }, [serverPreChatSurvey, submittingServerPreChat, acquireApiToken, account, appendLiveToken, handleLiveEnded, enterServerLiveMode]);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPending(false);
    setMessages(prev => prev.map(m =>
      m.id === activeStreamMessageId
        ? { ...m, completedAt: m.completedAt ?? Date.now() }
        : m,
    ));
    setActiveStreamMessageId(null);
    focusComposer();
  }, [activeStreamMessageId, focusComposer]);

  const restartChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    // Tear down any live-agent poll loop and clear the session binding.
    liveEventsAbortRef.current?.abort();
    liveEventsAbortRef.current = null;
    liveSessionIdRef.current = null;
    liveConversationIdRef.current = null;
    setPending(false);
    setActiveStreamMessageId(null);
    // Starting a fresh chat: drop the persisted conversation id so a later refresh doesn't resume
    // the conversation the user just abandoned.
    clearPersistedConversationId();
    setConversationId(undefined);
    setInitChatDone(false);
    liveMessageGateRef.current.reset();
    conversationGenerationRef.current += 1;
    initChatPromiseRef.current = null;
    messagesRef.current = [];
    setMessages([]);
    setAgentIntroduction(null);
    setDraft('');
    setLiveAgentName(null);
    setAgentSeen(false);
    sentCountRef.current = 0;
    setLastTrace(null);
    setLastEscalationDiagnostics(null);
    setMode('ai');
    setRecoveryState('active');
    setLivePresentation(initialLivePresentation);
    setChatEnded(false);
    setConfirmClose(false);
    setMinimized(false);
    setFeedbackOpen(false);
    setFeedbackRating(null);
    setFeedbackComment('');
    setFeedbackSent(false);
    setServerPreChatSurvey(null);
    setSubmittingServerPreChat(false);
    setServerPostChatSurvey(null);
    focusComposer();
  }, [focusComposer]);

  // Reset the conversation whenever the signed-in identity changes — signing in, signing out,
  // or switching accounts. Without this, an in-memory transcript (and the conversationId bound
  // to the previous identity) survives a sign-out because logoutRedirect doesn't always trigger
  // a full remount (e.g. the widget is embedded in a host page). Skip the initial mount so an
  // already-anonymous or already-signed-in first render doesn't wipe a freshly-started chat.
  const prevHomeAccountIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // Initial MSAL cache hydration is not an account switch.
    if (isAuthPending) return;
    const current = account?.homeAccountId ?? null;
    if (prevHomeAccountIdRef.current === undefined) {
      prevHomeAccountIdRef.current = current;
      return;
    }
    if (prevHomeAccountIdRef.current !== current) {
      prevHomeAccountIdRef.current = current;
      restartChat();
    }
  }, [account?.homeAccountId, isAuthPending, restartChat]);

  const selectLayoutMode = useCallback((nextMode: LayoutMode) => {
    setLayoutMode(nextMode);
    setLayoutMenuOpen(false);
    // Leaving float (or re-selecting it) clears any dragged position so the new dock anchors cleanly.
    setFloatPos(null);
  }, []);

  // Drag-to-move the floating window by its title bar. Constrained to the visible viewport,
  // accounting for the window's current width/height so it can never be dragged off-screen.
  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Only draggable in float mode; ignore clicks on header buttons (icons handle their own actions).
    if (layoutMode !== 'float') return;
    if ((e.target as HTMLElement).closest('button')) return;
    const el = windowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originLeft: rect.left,
      originTop: rect.top,
    };
    // Pin the window to its current pixel position so dragging is smooth from the first move.
    setFloatPos({ left: rect.left, top: rect.top });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [layoutMode]);

  const onHeaderPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    const el = windowRef.current;
    if (!drag || !el) return;
    const rect = el.getBoundingClientRect();
    const maxLeft = Math.max(0, window.innerWidth - rect.width);
    const maxTop = Math.max(0, window.innerHeight - rect.height);
    const nextLeft = Math.min(Math.max(0, drag.originLeft + (e.clientX - drag.startX)), maxLeft);
    const nextTop = Math.min(Math.max(0, drag.originTop + (e.clientY - drag.startY)), maxTop);
    setFloatPos({ left: nextLeft, top: nextTop });
  }, []);

  const onHeaderPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }, []);

  // Keep the dragged window inside the viewport when it is resized.
  useEffect(() => {
    if (!floatPos) return;
    const clamp = () => {
      const el = windowRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const maxLeft = Math.max(0, window.innerWidth - rect.width);
      const maxTop = Math.max(0, window.innerHeight - rect.height);
      setFloatPos(p => p ? { left: Math.min(p.left, maxLeft), top: Math.min(p.top, maxTop) } : p);
    };
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [floatPos]);

  const send = useCallback(async (
    messageOverride?: string,
    opts?: {
      silent?: boolean;
      /**
       * Answer to a server-issued ChatPrompt. Reuses this function's entire streaming path — a
       * prompt answer is an ordinary turn, so it gets the same conversation bootstrap, the same
       * event handling and the same transcript treatment as a typed message. The visible user
       * bubble is the label of what the customer chose, supplied via `messageOverride`.
       */
      promptResponse?: ChatPromptResponse;
    },
  ) => {
    const promptResponse = opts?.promptResponse;
    const text = (messageOverride ?? draft).trim();
    if ((!text && !promptResponse) || pending || liveSendBlocked) return;
    // Silent sends (e.g. the CSAT survey) reach the backend but render no chat bubbles — the
    // surface that triggered them shows its own confirmation.
    const silent = opts?.silent ?? false;
    const userMessageId = crypto.randomUUID();

    // Client-side /duration command — display last turn's trace without calling server.
    if (text === '/duration') {
      setDraft('');
      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', text }]);
      setMessages(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'agent',
          text: lastTrace ?? 'No trace available yet. Send a message first, then try /duration.',
          isTrace: !!lastTrace,
        },
      ]);
      return;
    }

    // Don't display /thumbdown, /thumbup, /thumbclear or /csat as visible user messages — they're system-initiated
    const isInternalCommand = silent
      || text.startsWith('/thumbdown') || text.startsWith('/thumbup')
      || text.startsWith('/thumbclear') || text.startsWith('/csat');
    if (!isInternalCommand) {
      // A new user-initiated message starts a fresh Q&A thread — reset the thumbs-down streak.
      downCountRef.current = 0;
      if (mode === 'live-agent') {
        setAgentSeen(false);
        sentCountRef.current = null;
      }
      setDraft('');
      setMessages(prev => [...prev, {
        id: userMessageId, clientOperationId: userMessageId, role: 'user', text, sentAt: Date.now(),
        pendingSend: mode === 'live-agent' && !!conversationId && isBrowserLiveConversation(conversationId),
      }]);
    }

    // In live-agent mode the message is sent via /chat/stream below — the server routes it to the
    // Omnichannel human-agent client. The SPA only ever talks to the API via init + stream.

    setPending(true);
    const sendTime = Date.now();
    let clearPendingInFinally = true;
    try {
      // Bearer is optional for own-SPA anonymous users (middleware trusts same-origin).
      const authBearer = await acquireApiToken();
      let effectiveConversationId = conversationId;
      let initializedThisTurn = false;
      if (!initChatDone || !effectiveConversationId) {
        const initializedConversationId = await callInitChat();
        if (initializedConversationId) {
          effectiveConversationId = initializedConversationId;
          initializedThisTurn = true;
        }
      }
      const commonArgs = {
        message: text,
        clientOperationId: userMessageId,
        conversationId: effectiveConversationId,
        authBearer: authBearer ?? undefined,
        homeAccountId: account?.homeAccountId,
        username: account?.username,
        displayName: account?.name,
        useInitChat: initChatDone || initializedThisTurn,
        entryPoint: entryPointOverride,
        agentName: agentNameOverride,
        promptResponse,
      };

      const streamMsgId = crypto.randomUUID();
      // A slash-command reply gets no feedback bar — EXCEPT a thumbs-down regenerate, which
      // produces a real regenerated answer the user can rate. Plain /thumbup and /thumbdown
      // return only an acknowledgement ("Thanks for your feedback"), so those must not show thumbs.
      const isRegenerate = text.startsWith('/thumbdown') && text.includes('regenerate=true');
      const isSlashCmd = text.startsWith('/') && !isRegenerate;
      setActiveStreamMessageId(streamMsgId);
      // Silent sends create no visible agent bubble; the streamed ack is discarded (onToken/onDone
      // updates simply find no matching message), so the survey/dialog can show its own thank-you.
      if (!silent) {
        setMessages(prev => [
          ...prev,
          { id: streamMsgId, role: 'agent' as const, text: '', sentAt: sendTime, isSlashCommandResponse: isSlashCmd || undefined },
        ]);
      }

      abortRef.current = sendChatMessageStream(commonArgs, {
        onStart: (evt: StreamEvent) => {
          if (evt.conversationId) {
            setConversationId(evt.conversationId);
            setPersistedConversationId(evt.conversationId);
          }
        },
        onProgress: (step: ProgressStep) => {
          setMessages(prev =>
            prev.map(m => {
              if (m.id !== streamMsgId) return m;
              // Ignore a step that repeats one already shown. The server de-duplicates per turn, but a
              // reconnect can replay a frame, and a repeated line reads as the agent going in circles.
              const existing = m.progressSteps ?? [];
              if (existing.some(s => s.sequence === step.sequence && s.text === step.text)) return m;
              return { ...m, progressSteps: [...existing, step] };
            }),
          );
        },
        onToken: (chunk, metadata) => {
          const firstResponseAt = Date.now();
          setMessages(prev =>
            prev.map(m =>
              m.id === streamMsgId
                ? {
                    ...m,
                    text: m.text + chunk,
                    caseDetails: readCaseDetails(metadata?.caseDetails) ?? m.caseDetails,
                    statusText: undefined,
                    receivedAt: m.receivedAt ?? firstResponseAt,
                    // Stamped on the first token only: the narration covers the wait BEFORE the answer,
                    // so the elapsed time it reports must stop where the answer starts.
                    progressElapsedMs:
                      m.progressElapsedMs ?? (m.sentAt ? firstResponseAt - m.sentAt : undefined),
                  }
                : m,
            ),
          );
        },
        // Ephemeral progress waypoint (e.g. "Getting a specialist…" or a Cortex activity
        // update). Shown transiently on the streaming placeholder until reply tokens arrive.
        onStatus: (text: string) => {
          setMessages(prev =>
            prev.map(m => (m.id === streamMsgId ? { ...m, statusText: text } : m)),
          );
        },
        onLiveToken: (evt: StreamEvent) => appendLiveToken(evt, `${account?.homeAccountId}:${effectiveConversationId}`),
        onDone: (evt: StreamEvent) => {
          if (evt.mode !== 'prechat-survey' && !evt.retryMessage) {
            setMessages(previous => revealLiveSend(previous, userMessageId));
          }
          const acknowledgement = confirmedReadIdentity(evt);
          if (acknowledgement.messageId) setMessages(previous =>
            previous.map(message => message.id === userMessageId ? { ...message, ...acknowledgement } : message));
          // The server processed the turn, so the step token really is spent — whether the step
          // succeeded or was rejected. A retryable failure comes back with a freshly issued prompt
          // carrying its own token, so nothing needs un-consuming here.
          inFlightPromptTokenRef.current = null;
          if (evt.conversationId) {
            setConversationId(evt.conversationId);
            setPersistedConversationId(evt.conversationId);
          }
          if (evt.trace) setLastTrace(evt.trace);
          if (evt.escalationDiagnostics) setLastEscalationDiagnostics(evt.escalationDiagnostics);
          const doneText = evt.text;
          const doneCitations = evt.citations;
          setMessages(prev =>
            prev.map(m => {
              if (m.id !== streamMsgId) return m;
              let finalText = m.text.length === 0 && doneText ? doneText : m.text;
              // Prefer server-side citations; otherwise fall back to a model-written
              // "Sources" block parsed out of the message body.
              let citations = doneCitations && doneCitations.length > 0 ? doneCitations : m.citations;
              // Strip any model-written Sources block (markdown links / bare URLs) from the
              // body so sources render behind the collapsible "Sources" link, not inline.
              const sourcesResult = extractSourcesBlock(finalText);
              if (sourcesResult.sources.length > 0) {
                finalText = sourcesResult.text;
                if (!(citations && citations.length > 0)) {
                  citations = sourcesResult.sources;
                }
              }
              // Legacy numbered "[1] ..." format fallback when server citations exist.
              if (citations && citations.length > 0) {
                finalText = finalText.replace(/\r\n/g, '\n').replace(/\n*\s*Sources\s*\n(\s*\[\d+\][^\n]*\n*)+\s*$/i, '').trimEnd();
              }
              const extracted = extractSuggestedPrompts(finalText);
              return {
                ...m,
                text: extracted.text,
                suggestedPrompts: extracted.suggestedPrompts.length > 0 ? extracted.suggestedPrompts : undefined,
                citations: citations && citations.length > 0 ? citations : m.citations,
                turnId: evt.turnId ?? m.turnId,
                feedbackEligible: evt.feedbackEligible ?? false,
                availableModalities: evt.availableModalities ?? undefined,
                eligibilityDevices: evt.eligibilityDevices ?? undefined,
                classificationPrompt: evt.classificationPrompt ?? undefined,
                prompt: evt.prompt ?? undefined,
                receivedAt: m.receivedAt ?? Date.now(),
                completedAt: Date.now(),
                requiresAuth: false,
                // The agent asked to hand off to a human (request_human_handoff → channelAction). When the
                // server-side live-agent bridge is enabled this turn also carries mode="live" and the
                // placeholder is replaced by the live session below; when it is off (live transfer is an
                // upcoming feature) the turn stays a normal message and we surface the Contact Support card.
                isHandoff: evt.channelAction === 'handoff_to_omnichannel',
              };
            }),
          );
          const isEscalationChoiceTurn =
            evt.channelAction === 'classification_confirmation_required'
            || evt.channelAction === 'escalation_requested'
            || evt.channelAction === 'escalation_started'
            || !!evt.classificationPrompt
            || (evt.availableModalities?.length ?? 0) > 0
            || (evt.eligibilityDevices?.length ?? 0) > 0;
          if (isEscalationChoiceTurn) {
            leaveServerLiveMode();
          }
          if (evt.channelAction === 'conversation_ended') {
            leaveServerLiveMode();
            setChatEnded(true);
          }
          if (evt.mode === 'prechat-survey' && evt.preChatSurvey) {
            // A pre-chat survey surfaced on the handoff turn (rare — normally it arrives on the first live
            // poll). Render it; submitting it sends the answers as a /chat/stream turn to start the session.
            const liveConversationId = evt.conversationId ?? conversationId ?? '';
            setServerPreChatSurvey({ survey: evt.preChatSurvey, conversationId: liveConversationId });
            setMessages(prev => prev.filter(m => !(m.id === streamMsgId && (m.text?.length ?? 0) === 0)));
          } else if (!isEscalationChoiceTurn && evt.mode === 'live' && evt.sessionId) {
            // The orchestrator already marked this conversation handed off. Enter live mode (idempotent)
            // and poll for the agent's replies. Remove the streamed placeholder if it's empty (a live-mode
            // forward produces no AI tokens) — but keep it when the handoff turn produced a reply.
            const liveConversationId = evt.conversationId ?? conversationId ?? '';
            if (!liveSessionIdRef.current && evt.liveSdkEnabled !== false) {
              // First transition into live mode for this conversation — the AI just handed off. Call the
              // SAME escalation entry point (POST /chat/init, action:"escalation") a BYOA/static caller
              // uses to actually connect to the human agent, passing the transcript so far. This is a real
              // HTTP round trip, not just an in-process marker — falls back to the existing poll-driven
              // flow (still starts the session on its own) if the call fails.
              void callEscalationInit(liveConversationId, messagesRef.current).then((escalation) => {
                if (escalation?.greeting) {
                  setMessages(prev => [...prev, {
                    id: crypto.randomUUID(), role: 'agent', text: botTextToMarkdown(escalation.greeting!), isLive: true, liveCategory: 'agent',
                  }]);
                }
                enterServerLiveMode(escalation?.conversationId ?? evt.sessionId!, liveConversationId);
              });
            } else {
              // Already live or authoritatively bridge-owned: poll the existing session, never re-escalate.
              enterServerLiveMode(evt.sessionId, liveConversationId);
            }
            setMessages(prev => prev.filter(m => !(m.id === streamMsgId && (m.text?.length ?? 0) === 0)));
          } else if (liveSessionIdRef.current && (evt.mode === 'ai' || evt.mode === 'ended')) {
            // A live turn (user send) ended the session — drop the empty placeholder and exit live mode.
            // mode="ended" means the human agent closed the whole conversation (terminal); "ai" is a
            // transient session-gone fallback.
            setMessages(prev => prev.filter(m => m.id !== streamMsgId));
            handleLiveEnded(evt, evt.mode === 'ended');
          } else if (liveSessionIdRef.current) {
            // Still in live mode after a forwarded turn: the agent's reply arrives asynchronously on the
            // poll loop, so this turn produced no AI tokens. Unconditionally drop the empty streamed
            // placeholder, otherwise its animated typing-dots would stay on screen forever.
            setMessages(prev => prev.filter(m => !(m.id === streamMsgId && (m.text?.length ?? 0) === 0)));
            if (evt.retryMessage && !isInternalCommand && liveResendCountRef.current < 5) {
              // The message wasn't delivered (OC session warming on another replica). Re-send it once
              // after a short back-off; the loop self-terminates when warm-up finishes (no more retryMessage).
              liveResendCountRef.current += 1;
              const resendText = text;
              setTimeout(() => { if (liveSessionIdRef.current) void send(resendText); }, 1500);
            } else {
              liveResendCountRef.current = 0;
            }
          }
          setPending(false);
          setActiveStreamMessageId(null);
          abortRef.current = null;
          focusComposer();
        },
        onLiveReady: (evt: StreamEvent) => {
          const liveConversationId = evt.conversationId ?? effectiveConversationId;
          if (evt.sessionId && liveConversationId) enterServerLiveMode(evt.sessionId, liveConversationId);
        },
        onError: (err: Error) => {
          const scanFeedback = liveMessageScanFeedback(err, EN_STRINGS);
          if (isTerminalLiveFailure(err)) {
            setRecoveryState('failed');
            setLivePresentation(previous => reduceLivePresentation(previous, {
              liveCategory: 'error', recoveryState: 'failed',
            }));
          }
          if (isUncertainLiveSend(err)) {
            setMessages(prev => prev.filter(message => message.id !== streamMsgId).map(message =>
              message.id === userMessageId ? { ...message, ...uncertainLiveSendIdentity(err, message) } : message));
          }
          if (effectiveConversationId && isBrowserLiveConversation(effectiveConversationId) && !isUncertainLiveSend(err)) {
            setMessages(prev => prev.filter(message => message.id !== userMessageId));
            if (!isInternalCommand) setDraft(current => current || text);
          }
          // Transport failure — the server may never have seen the turn, so give the control back.
          // If it did land, the server's own single-use check rejects the retry, which is a far
          // better outcome than leaving the customer with no way to continue.
          restoreInFlightPrompt();
          setMessages(prev =>
            prev.map(m =>
              m.id === streamMsgId
                ? {
                    ...m,
                    text: scanFeedback ?? 'Sorry, something went wrong. Try again in a moment.',
                    ...(scanFeedback ? { role: 'system' as const } : {}),
                    // Drop the narration: the turn produced no answer, so a "3 steps" trace under an
                    // error would claim work that led nowhere.
                    progressSteps: undefined,
                    progressElapsedMs: undefined,
                  }
                : m,
            ),
          );
          // eslint-disable-next-line no-console
          console.error(err);
          setPending(false);
          setActiveStreamMessageId(null);
          abortRef.current = null;
          focusComposer();
        },
      });
      clearPendingInFinally = false;
      return;
    } catch (err) {
      if (conversationId && isBrowserLiveConversation(conversationId) && !isInternalCommand) {
        setMessages(previous => previous.filter(message => message.id !== userMessageId));
        setDraft(current => current || text);
      }
      if (conversationId && isBrowserLiveConversation(conversationId) && isTerminalLiveFailure(err)) {
        setRecoveryState('failed');
        setLivePresentation(previous => reduceLivePresentation(previous, {
          liveCategory: 'error', recoveryState: 'failed',
        }));
      }
      restoreInFlightPrompt();
      setMessages(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'agent',
          text: 'Sorry, something went wrong. Try again in a moment.',
        },
      ]);
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      if (clearPendingInFinally) {
        setPending(false);
      }
      focusComposer();
    }

    function restoreInFlightPrompt() {
      const inFlight = inFlightPromptTokenRef.current;
      if (!inFlight) return;

      inFlightPromptTokenRef.current = null;
      setConsumedPromptTokens(prev => {
        const restored = new Set(prev);
        restored.delete(inFlight);
        return restored;
      });
    }
  }, [account, acquireApiToken, callEscalationInit, callInitChat, conversationId, draft, entryPointOverride, agentNameOverride, enterServerLiveMode, focusComposer, initChatDone, isSignedIn, lastTrace, leaveServerLiveMode, mode, pending, liveSendBlocked]);

  /**
   * Answers a server-issued prompt.
   *
   * Replaces the four bespoke handlers this used to need (modality select, classification confirm,
   * eligibility device select, callback submit) — each of which owned its own URL, its own request
   * shape and its own optimistic message bookkeeping. All of that is now one ordinary turn on the
   * chat stream, so a new escalation step needs no change here at all.
   */
  const submitPrompt = useCallback(async (
    prompt: ChatPrompt,
    values: Record<string, string | boolean>,
  ) => {
    if (pending || consumedPromptTokens.has(prompt.stepToken)) return;

    // The prompt is deliberately NOT removed from the message: on a failed or dropped turn the
    // failure frame carries no replacement, so removing it would strand the customer. Instead the
    // token is marked consumed, which disables the control without discarding it — and is undone
    // below if the request never reached the server.
    inFlightPromptTokenRef.current = prompt.stepToken;
    setConsumedPromptTokens(prev => new Set(prev).add(prompt.stepToken));

    const chosen = prompt.kind === 'choice' && typeof values.optionId === 'string'
      ? prompt.options?.find(option => option.id === values.optionId)?.label
      : undefined;

    // Form submissions have no option label, and their values may contain a phone number, so use a
    // fixed non-PII summary rather than echoing what was typed.
    const bubble = chosen
      ?? (prompt.kind === 'form' ? 'Submitted the requested details' : 'Continue');

    await send(bubble, { promptResponse: { verb: prompt.verb, stepToken: prompt.stepToken, values } });
  }, [pending, consumedPromptTokens, send]);


  // Submits (or clears) feedback for a turn via the backend slash-command handler, guarding against
  // concurrent/duplicate clicks with a per-message processing flag. rating === null clears it.
  // opts.regenerate sends a thumbs-down that also regenerates the answer; opts.comment attaches a reason.
  const submitFeedback = useCallback(async (
    messageId: string,
    turnId: string,
    rating: FeedbackRating | null,
    opts?: { comment?: string; regenerate?: boolean },
  ) => {
    setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, isFeedbackProcessing: true } : m)));
    try {
      if (rating === null) {
        await send(`/thumbclear turnId=${turnId}`);
      } else if (rating === 'up') {
        await send(`/thumbup turnId=${turnId}`);
      } else {
        const regen = opts?.regenerate ? ' regenerate=true' : '';
        const safe = opts?.comment ? ` comment="${opts.comment.replace(/"/g, "'")}"` : '';
        await send(`/thumbdown turnId=${turnId}${regen}${safe}`);
      }
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, feedback: rating, isFeedbackProcessing: false } : m)));
    } catch {
      // Roll back the processing flag; leave the prior feedback value untouched.
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, isFeedbackProcessing: false } : m)));
    }
  }, [send]);

  // Handles a thumb click.
  //  • 👍 records positive feedback and resets the thumbs-down streak.
  //  • 👎 regenerates the answer on the first two consecutive downs; the THIRD down in the same
  //    thread opens the reason dialog instead of regenerating.
  //  • Clicking an already-selected thumb removes the feedback.
  const handleCopy = useCallback((messageId: string) => {
    const msg = messagesRef.current.find(m => m.id === messageId);
    if (!msg) return;
    const text = stripSourcesForDisplay(msg.text ?? '').trim();
    if (!text) return;
    const markCopied = () => {
      setCopiedMsgId(messageId);
      if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
      copiedResetRef.current = window.setTimeout(() => setCopiedMsgId(null), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      });
    } else {
      // Fallback for insecure contexts / older browsers without the async clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        markCopied();
      } catch {
        // eslint-disable-next-line no-console
        console.error('Copy to clipboard failed');
      }
    }
  }, []);

  useEffect(() => () => {
    if (copiedResetRef.current) window.clearTimeout(copiedResetRef.current);
  }, []);

  // Transcript download. The server record is preferred because the browser only holds what it
  // rendered in THIS page view: a conversation resumed after a refresh, or the human live-agent leg
  // of a handed-off chat, exists only server-side. A conversation with no server record yet (or a
  // call that fails) falls back to what is on screen, so the control degrades rather than breaks.
  const [transcriptBusy, setTranscriptBusy] = useState(false);
  const handleDownloadTranscript = useCallback(async () => {
    const convId = conversationId;
    if (!convId) return;
    setTranscriptBusy(true);
    try {
      let entries: TranscriptEntry[] | null = null;
      try {
        const bearer = isSignedIn ? await acquireApiToken() : null;
        const server = await fetchTranscript(convId, bearer);
        if (server && server.entries.length > 0) {
          entries = server.entries.map(e => ({
            role: e.role,
            text: e.text,
            isLive: e.isLiveAgent,
            at: e.at ? Date.parse(e.at) : undefined,
            citations: e.citations ?? undefined,
          }));
        }
      } catch {
        entries = null;
      }

      if (!entries) {
        // `/duration` waterfalls are debug output, not conversation. Agent bodies keep their Sources
        // block until display, so strip it here exactly as the bubble does.
        entries = messagesRef.current
          .filter(m => !m.isTrace && !m.pendingSend)
          .map(m => ({
            role: m.role,
            text: m.role === 'agent' && !m.isLive
              ? stripSourcesForDisplay(m.text ?? '')
              : (m.text ?? ''),
            isLive: m.isLive,
            at: m.role === 'user' ? m.sentAt : (m.receivedAt ?? m.sentAt),
            citations: m.citations,
          }));
      }

      if (!entries.some(e => e.text.trim().length > 0)) return;
      // Labels the AI turns; live-agent turns are labelled by the transcript builder itself, so the
      // fixed assistant name is used rather than the header (which renames itself during a handoff).
      downloadTranscript(entries, { conversationId: convId, title: 'Support Assistant' });
    } finally {
      setTranscriptBusy(false);
    }
  }, [conversationId, isSignedIn, acquireApiToken]);

  const canDownloadTranscript = Boolean(conversationId) && messages.some(m => !m.isTrace && (m.text ?? '').trim().length > 0);

  const handleFeedback = useCallback((messageId: string, turnId: string, clicked: FeedbackRating) => {
    const msg = messagesRef.current.find(m => m.id === messageId);
    if (!msg || msg.isFeedbackProcessing) return; // spec: ignore clicks while a save is in flight
    const current = msg.feedback ?? null;

    if (current === clicked) {
      void submitFeedback(messageId, turnId, null); // toggle off → remove
      return;
    }

    if (clicked === 'up') {
      downCountRef.current = 0;
      void submitFeedback(messageId, turnId, 'up');
      return;
    }

    // Thumbs down: regenerate the first two times, then ask for a reason on the third.
    if (downCountRef.current < 2) {
      downCountRef.current += 1;
      void submitFeedback(messageId, turnId, 'down', { regenerate: true });
    } else {
      setReasonDialog({ messageId, turnId });
    }
  }, [submitFeedback]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      liveEventsAbortRef.current?.abort();
    };
  }, []);

  // Open and send when a chat request is provided from the landing page / dashboard
  useEffect(() => {
   if (chatRequest) {
     setOpen(true);
     if (chatRequest.action === 'send' && !pending) {
       void send(chatRequest.message).then(() => onChatRequestConsumed?.());
     } else {
       onChatRequestConsumed?.();
     }
   }
  }, [chatRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  if (!open) {
    const dotColor = status === 'connected' ? '#10b981' : status === 'connecting' ? '#f59e0b' : '#ef4444';
    return (
      <button onClick={() => setOpen(true)} className="hover-zoom" style={styles.icon} aria-label="Open chat">
        <span style={styles.iconInner}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <SupportAvatar size={44} iconColor="#0078d4" background="#ffffff" dotColor={dotColor} />
          </span>
          <span style={styles.launcherText}>
            <span style={{ fontSize: 15, fontWeight: 650, lineHeight: 1.3 }}>Quick help?</span>
            <span style={{ fontSize: 15, fontWeight: 650, lineHeight: 1.3 }}>Chat with AI</span>
          </span>
        </span>
      </button>
    );
  }

  if (open && minimized) {
    const dotColor = status === 'connected' ? '#10b981' : status === 'connecting' ? '#f59e0b' : '#ef4444';
    return (
      <div style={styles.minimizedBar} role="toolbar" aria-label="Support Assistant (minimized)">
        <button
          onClick={() => setMinimized(false)}
          style={styles.minimizedAvatarBtn}
          title="Expand chat"
          aria-label="Expand chat"
        >
          <SupportAvatar size={30} dotColor={dotColor} />
        </button>
        <button
          onClick={() => { setMinimized(false); restartChat(); }}
          className="hover-header-icon"
          style={styles.headerIconButton}
          title="New chat"
          aria-label="Start a new chat"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v8M8 12h8" />
          </svg>
        </button>
        <button
          onClick={() => setMinimized(false)}
          className="hover-header-icon"
          style={styles.headerIconButton}
          title="Expand"
          aria-label="Expand"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 15l6-6 6 6" />
          </svg>
        </button>
        <button
          onClick={() => { if (chatEnded) { setOpen(false); restartChat(); } else { setMinimized(false); setConfirmClose(true); } }}
          className="hover-header-icon"
          style={styles.close}
          title="Close"
          aria-label="Close"
        >
          ×
        </button>
      </div>
    );
  }

  const headerLabel = mode !== 'live-agent' ? 'Support Assistant'
    : livePresentation.phase === 'connecting' ? EN_STRINGS.chrome.connectingTitle
      : livePresentation.phase === 'waiting' ? EN_STRINGS.chrome.waitingTitle
        : livePresentation.phase === 'failed' ? EN_STRINGS.chrome.failedTitle
          : liveAgentName ?? EN_STRINGS.chrome.liveTitle;
  const signedInName = userInfo?.givenName ?? userInfo?.displayName ?? account?.name ?? null;
  const headerSubtitle = mode === 'live-agent'
    ? livePresentation.phase === 'connecting' ? EN_STRINGS.chrome.connectingSubtitle
      : livePresentation.phase === 'waiting' ? EN_STRINGS.chrome.waitingSubtitle
        : livePresentation.phase === 'failed' ? EN_STRINGS.chrome.failedSubtitle
          : EN_STRINGS.chrome.liveSubtitle
    : isSignedIn
      ? (signedInName ? `Signed in as ${signedInName}` : 'Signed in for account help')
      : '';

  return (
    <div
      ref={windowRef}
      style={{
        ...styles.window(layoutMode, isMobile),
        ...(!isMobile && layoutMode === 'float' && floatPos
          ? { left: floatPos.left, top: floatPos.top, right: 'auto', bottom: 'auto' }
          : {}),
      }}
      role="dialog"
      aria-label={headerLabel}
    >
      {lastEscalationDiagnostics && (
        <span
          hidden
          aria-hidden="true"
          data-cmsp-diagnostic="peridot-escalation"
          data-cmsp-escalation-stage={lastEscalationDiagnostics.stage}
          data-cmsp-theme={lastEscalationDiagnostics.theme ?? undefined}
          data-cmsp-theme-l1={lastEscalationDiagnostics.themeL1 ?? undefined}
          data-cmsp-theme-l2={lastEscalationDiagnostics.themeL2 ?? undefined}
          data-cmsp-theme-l3={lastEscalationDiagnostics.themeL3 ?? undefined}
          data-cmsp-selected-modality={lastEscalationDiagnostics.selectedModality ?? undefined}
          data-cmsp-eligibility-source={lastEscalationDiagnostics.eligibilitySource ?? undefined}
          data-cmsp-eligibility-decision={lastEscalationDiagnostics.eligibilityDecision ?? undefined}
          data-cmsp-windows-supported={lastEscalationDiagnostics.isWindowsSupported ?? undefined}
          data-cmsp-warranty-active={lastEscalationDiagnostics.isWarrantyActive ?? undefined}
          data-cmsp-warranty-end-date-present={lastEscalationDiagnostics.warrantyEndDatePresent}
          data-cmsp-modality-source={lastEscalationDiagnostics.modalitySource ?? undefined}
          data-cmsp-routing-source={lastEscalationDiagnostics.routingSource ?? undefined}
          data-cmsp-error-code={lastEscalationDiagnostics.lastErrorCode ?? undefined}
          data-cmsp-failure-stage={lastEscalationDiagnostics.lastFailureStage ?? undefined}
          data-cmsp-failure-detail={lastEscalationDiagnostics.lastFailureDetail ?? undefined}
          data-cmsp-routing-info-latency-ms={lastEscalationDiagnostics.routingInfoLatencyMs ?? undefined}
          data-cmsp-routing-to-chat-latency-ms={lastEscalationDiagnostics.routingToHumanChatLatencyMs ?? undefined}
        />
      )}
      <header
        style={{ ...styles.header, ...(layoutMode === 'float' ? { cursor: 'move' } : {}) }}
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
      >
        <div style={styles.headerLeft}>
          <SupportAvatar size={30} iconColor="#0078d4" background="#e8f0fe" dotColor={status === 'connected' ? '#10b981' : status === 'connecting' ? '#f59e0b' : '#ef4444'} />
          <div style={styles.headerText}>
            <span style={styles.headerTitle}>{headerLabel}</span>
            {headerSubtitle && <span style={styles.headerSubtitle}>{headerSubtitle}</span>}
          </div>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={() => { void handleDownloadTranscript(); }}
            className="hover-header-icon"
            style={{ ...styles.headerIconButton, opacity: canDownloadTranscript && !transcriptBusy ? 1 : 0.4 }}
            title="Download transcript"
            aria-label="Download transcript"
            disabled={!canDownloadTranscript || transcriptBusy}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v10" />
              <path d="M8 11l4 4 4-4" />
              <path d="M5 18h14" />
            </svg>
          </button>
          <button onClick={restartChat} className="hover-header-icon" style={styles.headerIconButton} title="New chat" aria-label="Start a new chat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v8M8 12h8" />
            </svg>
          </button>
          {!isMobile && (
            <div style={styles.layoutMenuWrapper}>
              <button
                onClick={() => setLayoutMenuOpen(prev => !prev)}
                className="hover-header-icon"
                style={styles.headerIconButton}
                title="Layout"
                aria-label="Change chat window layout"
                aria-expanded={layoutMenuOpen}
              >
                ◱
              </button>
              {layoutMenuOpen && (
                <div style={styles.layoutMenu} role="menu">
                  {layoutOptions.map(option => (
                    <button
                      key={option.mode}
                      type="button"
                      onClick={() => selectLayoutMode(option.mode)}
                      style={styles.layoutMenuItem(option.mode === layoutMode)}
                      role="menuitem"
                    >
                      <span>{option.label}</span>
                      {option.mode === layoutMode && <span aria-hidden="true">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => { setMinimized(true); setReasonDialog(null); }}
            className="hover-header-icon"
            style={styles.headerIconButton}
            title="Minimize"
            aria-label="Minimize chat"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <button onClick={() => { if (chatEnded) { setOpen(false); restartChat(); } else { setMinimized(false); setConfirmClose(true); } }} className="hover-header-icon" style={styles.close} title="Close" aria-label="Close">×</button>
        </div>
      </header>

      {!minimized && (
        <div style={styles.bodyRow}>
          {isSignedIn && <ChatTabRail active={activeTab} onSelect={setActiveTab} />}
          {activeTab === 'chat' || !isSignedIn ? (
            <div style={styles.bodyContent}>
      <div ref={scrollRef} style={styles.scroll}>
        {agentIntroduction && (
          <section style={styles.agentIntroduction} aria-label="About this support agent">
            <div style={styles.agentIntroductionIcon} aria-hidden="true">
              <SupportPersonIcon size={20} color="#2563eb" />
            </div>
            <div>
              <div style={styles.agentIntroductionLabel}>Your support agent</div>
              <p style={styles.agentIntroductionText}>{agentIntroduction}</p>
            </div>
          </section>
        )}
        {messages.length === 0 && (
          <div style={styles.welcome}>
            <div style={styles.welcomeIcon}>✦</div>
            <strong>Hi, I’m your Microsoft support assistant.</strong>
            <p style={{ margin: '8px 0', color: '#4b5563' }}>
              How can I help?
            </p>
          </div>
        )}
        {(() => {
          // Suggestion chips (both SDK suggestedPrompts and live suggestedActions) are
          // stored per-message, so without this guard every past agent turn keeps rendering
          // its chips. Only surface suggestions on the most recent agent turn; once the user
          // picks a choice or types a custom reply, a newer message is appended and the prior
          // turn's stale chips disappear automatically.
          const lastAgentMsgId = [...messages].reverse().find(x => x.role === 'agent' && !x.isTrace)?.id;
          const lastUserMsgId = [...messages].reverse().find(x => x.role === 'user')?.id;
          const isLastAgentMsg = (m: (typeof messages)[number]) => m.id === lastAgentMsgId;
          const hasEscalationControl = (m: (typeof messages)[number]) =>
            m.role === 'agent'
            && !m.isTrace
            && !!m.prompt;
          return (
        <>
        {messages.filter(m => !m.pendingSend).map(m => (
          <div
            key={m.id}
            data-live-read-id={m.isLive && !m.isBot ? m.messageId : undefined}
            style={
              m.role === 'user' ? styles.userMsg
              : m.role === 'system' ? styles.systemMsg
              : styles.agentMsg
            }
          >
            {m.role === 'system' ? (
              <div
                style={styles.systemBubble}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {m.text}
              </div>
            ) : (
              <>
                {m.role === 'agent' && !m.isTrace && (
                  <div style={styles.messageMeta}>
                    {m.isBot ? EN_STRINGS.conversation.botLabel : m.isLive ? (m.senderName ?? liveAgentName ?? 'Live agent') : 'Assistant'}
                    {m.sentAt && m.receivedAt && (
                      <>
                        {' - '}
                        {new Date(m.receivedAt).toLocaleTimeString()}
                        {' · '}
                        <span style={{ color: (m.receivedAt - m.sentAt) > 5000 ? '#ef4444' : (m.receivedAt - m.sentAt) > 2000 ? '#f59e0b' : '#10b981' }}>
                          {((m.receivedAt - m.sentAt) / 1000).toFixed(1)}s
                        </span>
                      </>
                    )}
                  </div>
                )}
                  <div style={styles.messageRow(m.role as 'user' | 'agent')}>
                    {m.role === 'agent' && !m.isTrace && (
                      <div style={styles.avatar}>
                        {m.isLive
                          ? ((m.senderName ?? liveAgentName ?? 'L').trim().charAt(0).toUpperCase() || 'L')
                          : 'AI'}
                      </div>
                    )}
                    <div style={m.isTrace ? styles.traceBubble : styles.bubble(m.role as 'user' | 'agent')}>
                    {m.role === 'agent' && !m.isTrace
                      ? m.text
                        ? <CaseDetailsMessage text={m.isBot ? m.text : stripSourcesForDisplay(m.text)}
                            caseDetails={m.caseDetails} renderText={text => <MarkdownText text={text} />} />
: (hasEscalationControl(m) || m.liveCard || m.liveAuthCard || (m.liveAttachments && m.liveAttachments.length > 0) || (m.liveSuggestedActions && m.liveSuggestedActions.length > 0))
                          ? null
                          : (m.progressSteps && m.progressSteps.length > 0)
                            // The agent told us what it is doing, so show that instead of three dots.
                            // The dots stay the fallback for every agent that narrates nothing.
                            ? (
                              <>
                                <AgentActivitySteps steps={m.progressSteps} state="running" />
                                {/* A status waypoint is a different thing from a narration step — it
                                    reports a routing move rather than work in progress — so it keeps
                                    showing under the steps rather than being swallowed by them. */}
                                {m.statusText && (
                                  <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75, fontStyle: 'italic' }}>
                                    {m.statusText}
                                  </div>
                                )}
                              </>
                            )
                            : (
                            <div style={styles.activity}>
                              <span style={styles.activityDots} aria-hidden="true">
                                <span style={{ ...styles.activityDot, animationDelay: '0ms' }} />
                                <span style={{ ...styles.activityDot, animationDelay: '160ms' }} />
                                <span style={{ ...styles.activityDot, animationDelay: '320ms' }} />
                              </span>
                              {m.statusText && (
                                <span style={{ marginLeft: 8, fontSize: 13, opacity: 0.75, fontStyle: 'italic' }}>
                                  {m.statusText}
                                </span>
                              )}
                            </div>
                          )
                      : m.text}
                    {m.deliveryUncertain && <div role="status" data-delivery-uncertain>{EN_STRINGS.status.sendStatusUnknown}</div>}
                    {m.role === 'agent' && !m.isTrace && m.text && m.progressSteps && m.progressSteps.length > 0 && (
                      <AgentActivitySteps steps={m.progressSteps} state="done" elapsedMs={m.progressElapsedMs} />
                    )}
                    {hasEscalationControl(m) && m.prompt && (
                      <ChatPromptNonAgentModalityLinks
                        prompt={m.prompt}
                        disabled={pending || consumedPromptTokens.has(m.prompt.stepToken)}
                        onSubmit={(values) => void submitPrompt(m.prompt!, values)}
                      />
                    )}
                    {m.role === 'agent' && !m.isTrace && m.liveCard != null && (
                      <AdaptiveCardBubble
                        card={m.liveCard}
                        disabled={mode !== 'live-agent'}
                        onSubmit={(value) => void send(cardSubmitToText(value))}
                      />
                    )}
                    {m.role === 'agent' && !m.isTrace && m.liveAttachments && m.liveAttachments.length > 0 && (
                      <LiveAttachmentChips
                        attachments={m.liveAttachments}
                        conversationId={conversationId ?? null}
                        getAuth={getAttachmentAuth}
                      />
                    )}
                    {m.role === 'agent' && !m.isTrace && m.liveAuthCard && (
                      <SignInCard text={m.liveAuthCard.text} url={m.liveAuthCard.url} disabled={mode !== 'live-agent'} />
                    )}
                    {m.role === 'agent' && !m.isTrace && isLastAgentMsg(m) && m.liveSuggestedActions && m.liveSuggestedActions.length > 0 && (
                      <SuggestedActionChips
                        actions={m.liveSuggestedActions}
                        disabled={mode !== 'live-agent'}
                        onPick={(text) => void send(text)}
                      />
                    )}
                    {m.role === 'agent' && !m.isTrace && m.citations && m.citations.length > 0 && (
                      <CitationList citations={m.citations} />
                    )}
                    {hasEscalationControl(m) && m.prompt && (
                      <ChatPromptCard
                        prompt={m.prompt}
                        disabled={pending || consumedPromptTokens.has(m.prompt.stepToken)}
                        onSubmit={(values) => void submitPrompt(m.prompt!, values)}
                      />
                    )}
                    {m.requiresAuth && !isSignedIn && (
                      <div style={{ marginTop: 8 }}>
                        <button onClick={signIn} style={styles.authButton}>
                          Sign in to continue
                        </button>
                      </div>
                    )}
                    {m.isHandoff && mode !== 'live-agent' && (
                      <div style={styles.handoffCard}>
                        <div style={styles.handoffCardTitle}>Contact Support</div>
                        <div style={styles.handoffCardText}>
                          Upcoming feature transferring to Human agent
                        </div>
                      </div>
                    )}
                    {m.role === 'user' && receiptReadOn(m) && (
                      <div style={styles.seenLabel} aria-live="polite">{EN_STRINGS.conversation.seenBySupportLabel}</div>
                    )}
                    {m.role === 'user' && mode === 'live-agent' && agentSeen && m.id === lastUserMsgId
                      && (!conversationId || !isBrowserLiveConversation(conversationId)) && (
                      <div style={styles.seenLabel} aria-live="polite">Seen</div>
                    )}
                    {m.role === 'agent' && !m.isTrace && !m.isSlashCommandResponse && m.feedbackEligible && m.turnId && m.completedAt && (
                      <div style={styles.feedbackBar}>
                        <button
                          type="button"
                          aria-label={copiedMsgId === m.id ? 'Copied' : 'Copy'}
                          title={copiedMsgId === m.id ? 'Copied' : 'Copy'}
                          onClick={() => handleCopy(m.id)}
                          style={{
                            ...styles.feedbackButton,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(copiedMsgId === m.id ? styles.feedbackButtonActive : {}),
                          }}
                        >
                          {copiedMsgId === m.id ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          )}
                        </button>
                        {m.feedback !== 'down' && (
                          <button
                            type="button"
                            aria-label="Helpful"
                            aria-pressed={m.feedback === 'up'}
                            title="Helpful"
                            disabled={m.isFeedbackProcessing}
                            onClick={() => handleFeedback(m.id, m.turnId!, 'up')}
                            style={{
                              ...styles.feedbackButton,
                              ...(m.feedback === 'up' ? styles.feedbackButtonActive : {}),
                              ...(m.isFeedbackProcessing ? styles.feedbackButtonDisabled : {}),
                            }}
                          >
                            👍
                          </button>
                        )}
                        {m.feedback !== 'up' && (
                          <button
                            type="button"
                            aria-label="Not helpful"
                            aria-pressed={m.feedback === 'down'}
                            title="Not helpful"
                            disabled={m.isFeedbackProcessing}
                            onClick={() => handleFeedback(m.id, m.turnId!, 'down')}
                            style={{
                              ...styles.feedbackButton,
                              ...(m.feedback === 'down' ? styles.feedbackButtonActive : {}),
                              ...(m.isFeedbackProcessing ? styles.feedbackButtonDisabled : {}),
                            }}
                          >
                            👎
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {m.role === 'agent' && !m.isTrace && isLastAgentMsg(m) && m.suggestedPrompts && m.suggestedPrompts.length > 0 && (
                  <div style={styles.suggestedPrompts}>
                    {m.suggestedPrompts.map(prompt => (
                      <button
                        key={prompt}
                        type="button"
                        disabled={pending}
                        onClick={() => void send(prompt)}
                        style={styles.suggestedPromptButton}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
                {m.role === 'user' && m.sentAt && (
                  <div style={styles.timestamp}>
                    <span>{new Date(m.sentAt).toLocaleTimeString()}</span>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        </>
          );
        })()}
        {chatEnded && (
          <div style={styles.endedBlock}>
            <div style={styles.endedNotice}>Conversation closed</div>
            {!feedbackSent ? (
              <div style={styles.feedbackPrompt}>
                <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>We&apos;d love your feedback</div>
                <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 12 }}>
                  Answer a few questions to help us improve the Support Assistant.
                </div>
                <button type="button" className="hover-confirm-primary" style={styles.feedbackPromptButton} onClick={() => setFeedbackOpen(true)}>
                  Give feedback
                </button>
              </div>
            ) : (
              <div style={styles.feedbackPrompt}>
                <div style={{ fontWeight: 600, color: '#111827' }}>Thanks for your feedback!</div>
                <div style={{ fontSize: 13, color: '#4b5563', marginTop: 4 }}>
                  Your response helps us improve Microsoft products and services.
                </div>
              </div>
            )}
          </div>
        )}
        {serverPreChatSurvey && (
          <PreChatSurveyCard
            survey={serverPreChatSurvey.survey}
            disabled={submittingServerPreChat}
            onSubmit={(response) => void submitServerPreChatSurvey(response)}
          />
        )}
        {serverPostChatSurvey && (
          <div style={styles.endedBlock}>
            <div style={styles.feedbackPrompt}>
              <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>How was your support experience?</div>
              <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 12 }}>
                Please take a moment to rate your conversation with the live agent.
              </div>
              <a
                href={serverPostChatSurvey.url}
                target="_blank"
                rel="noreferrer"
                className="hover-confirm-primary"
                style={{ ...styles.feedbackPromptButton, display: 'inline-block', textDecoration: 'none' }}
              >
                Rate your experience
              </a>
            </div>
          </div>
        )}
      </div>

      <div style={styles.composer}>
        <div className="composer-box" style={styles.composerBox}>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              mode === 'live-agent'
                ? livePresentation.phase === 'connecting' ? EN_STRINGS.composer.connectingPlaceholder
                  : livePresentation.phase === 'waiting' ? EN_STRINGS.composer.waitingPlaceholder
                    : livePresentation.phase === 'failed' ? EN_STRINGS.composer.failedPlaceholder
                      : liveAgentName ? `Message ${liveAgentName}…` : EN_STRINGS.composer.livePlaceholder
              : isSignedIn ? 'Type a message…'
              : 'Ask anything ()…'
            }
            rows={1}
            style={styles.textarea}
            disabled={pending || chatEnded || !!serverPreChatSurvey || liveSendBlocked}
            aria-label="Message"
          />
          <div style={styles.composerFooter}>
            <span style={styles.composerHint}>Enter to send · Shift+Enter for a new line</span>
            {activeStreamMessageId ? (
              <button type="button" onClick={stopStreaming} style={styles.stop}>
                Stop
              </button>
            ) : (
              <button type="button" onClick={() => void send()} disabled={pending || chatEnded || liveSendBlocked || !draft.trim()} className={`hover-send${draft.trim() && !chatEnded ? ' send-active' : ''}`} style={{ ...styles.send, opacity: (pending || chatEnded || liveSendBlocked || !draft.trim()) ? 0.4 : 1 }} aria-label="Send" title="Send">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path className="send-regular" d="M5.69362 11.9997L2.29933 3.2715C2.0631 2.66403 2.65544 2.08309 3.2414 2.28959L3.33375 2.32885L21.3337 11.3288C21.852 11.588 21.8844 12.2975 21.4309 12.6129L21.3337 12.6705L3.33375 21.6705C2.75077 21.962 2.11746 21.426 2.2688 20.8234L2.29933 20.7278L5.69362 11.9997L2.29933 3.2715L5.69362 11.9997ZM4.4021 4.54007L7.01109 11.2491L13.6387 11.2497C14.0184 11.2497 14.3322 11.5318 14.3818 11.8979L14.3887 11.9997C14.3887 12.3794 14.1065 12.6932 13.7404 12.7428L13.6387 12.7497L7.01109 12.7491L4.4021 19.4593L19.3213 11.9997L4.4021 4.54007Z" />
                  <path className="send-filled" d="M12.8147 12.1969L5.28344 13.4521C5.10705 13.4815 4.95979 13.6029 4.89723 13.7704L2.29933 20.7278C2.05066 21.3673 2.72008 21.9773 3.33375 21.6705L21.3337 12.6705C21.8865 12.3941 21.8865 11.6052 21.3337 11.3288L3.33375 2.32885C2.72008 2.02201 2.05066 2.63206 2.29933 3.2715L4.89723 10.2289C4.95979 10.3964 5.10705 10.5178 5.28344 10.5472L12.8147 11.8024C12.9236 11.8205 12.9972 11.9236 12.9791 12.0325C12.965 12.1168 12.899 12.1829 12.8147 12.1969Z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div style={styles.disclaimer}>AI-generated content may be incorrect</div>
        {confirmClose && (
          <>
            <div style={styles.confirmScrim} onClick={() => setConfirmClose(false)} />
            <div style={styles.confirmSheet} role="dialog" aria-label="End Chat">
              <div style={styles.confirmTitle}>End Chat</div>
              <div style={styles.confirmBody}>Do you want to close this chat and start fresh?</div>
              <button
                type="button"
                className="hover-confirm-primary"
                style={styles.confirmPrimary}
                onClick={async () => {
                  const generation = conversationGenerationRef.current;
                  const scope = liveScopeRef.current;
                  const controller = abortRef.current;
                  const isCurrent = () => generation === conversationGenerationRef.current && scope === liveScopeRef.current;
                  setConfirmClose(false);
                  if (conversationId && isBrowserLiveConversation(conversationId)) {
                    setPending(true);
                    try { await endLiveSession(conversationId); }
                    catch {
                      if (!isCurrent()) return;
                      setMessages(prev => isCurrent() ? [...prev, {
                        id: crypto.randomUUID(), role: 'system',
                        text: 'Sorry, something went wrong. Try again in a moment.',
                      }] : prev);
                      setPending(false);
                      return;
                    }
                  }
                  if (!isCurrent()) return;
                  controller?.abort();
                  if (abortRef.current === controller) abortRef.current = null;
                  setPending(false);
                  setActiveStreamMessageId(null);
                  setDraft('');
                  setChatEnded(true);
                }}
              >
                End Chat
              </button>
              <button type="button" className="hover-confirm-secondary" style={styles.confirmSecondary} onClick={() => setConfirmClose(false)}>
                Cancel
              </button>
            </div>
          </>
        )}
        {feedbackOpen && (
          <>
            <div style={styles.confirmScrim} onClick={() => setFeedbackOpen(false)} />
            <div style={styles.feedbackCard} role="dialog" aria-label="Feedback to Microsoft">
              <div style={styles.feedbackCardHeader}>
                <span style={styles.feedbackCardTitle}>Feedback to Microsoft</span>
                <button type="button" className="hover-header-icon" style={styles.close} aria-label="Close" onClick={() => setFeedbackOpen(false)}>×</button>
              </div>
              <div style={styles.feedbackScroll}>
                <div style={styles.feedbackQuestion}>How satisfied are you with this Support Assistant experience?*</div>
                {[
                  { v: 5, label: '5 - Very Satisfied' },
                  { v: 4, label: '4 - Satisfied' },
                  { v: 3, label: '3 - Neutral' },
                  { v: 2, label: '2 - Dissatisfied' },
                  { v: 1, label: '1 - Very Dissatisfied' },
                ].map(opt => (
                  <label key={opt.v} style={styles.feedbackOption}>
                    <input
                      type="radio"
                      name="cmsp-feedback-rating"
                      checked={feedbackRating === opt.v}
                      onChange={() => setFeedbackRating(opt.v)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
                <div style={{ ...styles.feedbackQuestion, marginTop: 16 }}>Please tell us more. Why did you choose that answer?</div>
                <textarea
                  value={feedbackComment}
                  onChange={e => setFeedbackComment(e.target.value)}
                  placeholder="Please do not include any confidential or personal information in your comment"
                  style={styles.feedbackTextarea}
                  rows={4}
                />
                <div style={styles.feedbackPrivacy}>
                  By clicking Send, your feedback will be used to improve Microsoft products and services.{' '}
                  <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noreferrer" style={{ color: '#0078d4' }}>Privacy statement</a>
                </div>
              </div>
              <div style={styles.feedbackActions}>
                <button
                  type="button"
                  disabled={feedbackRating === null}
                  className="hover-feedback-send"
                  style={{ ...styles.feedbackSend, opacity: feedbackRating === null ? 0.5 : 1, cursor: feedbackRating === null ? 'not-allowed' : 'pointer' }}
                  onClick={() => {
                    if (feedbackRating === null) return;
                    const comment = feedbackComment.trim();
                    const safe = comment ? ` comment="${comment.replace(/"/g, "'")}"` : '';
                    void send(`/csat score=${feedbackRating}${safe}`, { silent: true });
                    setFeedbackOpen(false);
                    setFeedbackSent(true);
                  }}
                >
                  Send
                </button>
                <button type="button" className="hover-feedback-cancel" style={styles.feedbackCancel} onClick={() => setFeedbackOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
            </div>
          ) : (
            <ChatInfoPanel key={account?.homeAccountId} tab={activeTab} userInfo={userInfo} getAuthArgs={getPanelAuthArgs} />
          )}
        </div>
      )}
      {reasonDialog && (
        <FeedbackReasonDialog
          onCancel={() => setReasonDialog(null)}
          onSubmit={(comment) => {
            const { messageId, turnId } = reasonDialog;
            setReasonDialog(null);
            downCountRef.current = 0;
            void submitFeedback(messageId, turnId, 'down', { comment });
          }}
        />
      )}
    </div>
  );
}

const NEGATIVE_FEEDBACK_REASONS = [
  'Incorrect answer',
  'Not helpful',
  'Too long',
  'Too short',
  'Outdated information',
  'Offensive content',
  'Other',
] as const;

/** Negative-feedback reason dialog ("What went wrong?"). Collects checkboxes + optional detail. */
function FeedbackReasonDialog({
  onSubmit,
  onCancel,
}: {
  onSubmit: (comment: string) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>({ Other: true });
  const [detail, setDetail] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the detail box when the dialog opens (Other is checked by default).
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const toggle = (reason: string) =>
    setSelected(prev => ({ ...prev, [reason]: !prev[reason] }));

  const submit = () => {
    const reasons = NEGATIVE_FEEDBACK_REASONS.filter(r => selected[r]);
    const parts: string[] = [];
    if (reasons.length > 0) parts.push(reasons.join(', '));
    if (detail.trim()) parts.push(detail.trim());
    onSubmit(parts.join(' — '));
  };

  return (
    <div style={styles.reasonOverlay} role="dialog" aria-modal="true" aria-label="Feedback reason">
      <div style={styles.reasonDialog}>
        <div style={styles.reasonTitle}>What went wrong?</div>
        <div style={styles.reasonList}>
          {NEGATIVE_FEEDBACK_REASONS.map(reason => (
            <label key={reason} style={styles.reasonItem}>
              <input
                type="checkbox"
                checked={!!selected[reason]}
                onChange={() => toggle(reason)}
              />
              <span>{reason}</span>
            </label>
          ))}
        </div>
        <textarea
          ref={textareaRef}
          value={detail}
          onChange={e => setDetail(e.target.value)}
          placeholder="Add more detail (optional). Please don't include personal information."
          style={styles.reasonTextarea}
          rows={3}
        />
        <div style={styles.reasonActions}>
          <button type="button" onClick={onCancel} style={styles.reasonCancel}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={styles.reasonSubmit}>
            Send feedback
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  icon: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    background: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: 28,
    padding: '12px 40px 12px 14px',
    cursor: 'pointer',
    boxShadow: '0 10px 28px rgba(0,120,212,0.32)',
    fontSize: 14,
  } as React.CSSProperties,
  iconInner: { display: 'flex', alignItems: 'center', gap: 16 } as React.CSSProperties,
  launcherBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
  } as React.CSSProperties,
  launcherText: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', color: 'white', gap: 2, whiteSpace: 'nowrap' } as React.CSSProperties,
  minimizedBar: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '6px 10px',
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
    zIndex: 1000,
  } as React.CSSProperties,
  minimizedAvatarBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: 999,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
  } as React.CSSProperties,
  window: (layoutMode: LayoutMode, isMobile = false): React.CSSProperties => ({
    position: 'fixed',
    ...(isMobile ? {
      inset: 0,
      width: '100%',
      height: '100%',
      minWidth: 0,
      borderRadius: 0,
    } : layoutMode === 'float' ? {
      bottom: 24,
      right: 24,
      width: '25.3vw',
      minWidth: 396,
      height: '85vh',
      borderRadius: 16,
    } : layoutMode === 'left' ? {
      top: 16,
      bottom: 16,
      left: 16,
      width: 420,
      borderRadius: 16,
    } : layoutMode === 'right' ? {
      top: 16,
      right: 16,
      bottom: 16,
      width: 420,
      borderRadius: 16,
    } : {
      top: 12,
      right: 12,
      bottom: 12,
      left: 12,
      borderRadius: 18,
    }),
    background: 'white',
    border: '1px solid #e5e7eb',
    boxShadow: '0 24px 70px rgba(15,23,42,0.24)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Segoe UI, system-ui, sans-serif',
    overflow: 'hidden',
    zIndex: 1000,
  }),
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  } as React.CSSProperties,
  headerLeft: { display: 'flex', alignItems: 'center' } as React.CSSProperties,
  headerText: { display: 'flex', flexDirection: 'column', marginLeft: 10 } as React.CSSProperties,
  headerTitle: { fontWeight: 650, fontSize: 14, color: '#111827' } as React.CSSProperties,
  headerSubtitle: { fontSize: 11, color: '#6b7280', marginTop: 1 } as React.CSSProperties,
  headerActions: { display: 'flex', alignItems: 'center', gap: 8, position: 'relative' } as React.CSSProperties,
  close: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 999,
    fontSize: 22,
    cursor: 'pointer',
    color: '#6b7280',
    lineHeight: 1,
  } as React.CSSProperties,
  bodyRow: { flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' } as React.CSSProperties,
  bodyContent: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } as React.CSSProperties,
  scroll: { flex: 1, overflowY: 'auto', padding: '14px 14px 10px', background: '#f8fafc' } as React.CSSProperties,
  agentIntroduction: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 14,
    padding: '14px 16px',
    border: '1px solid #bfdbfe',
    borderRadius: 14,
    background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
    boxShadow: '0 4px 12px rgba(37,99,235,0.08)',
  } as React.CSSProperties,
  agentIntroductionIcon: {
    width: 36,
    height: 36,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    background: '#dbeafe',
  } as React.CSSProperties,
  agentIntroductionLabel: {
    marginBottom: 4,
    color: '#1e40af',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  } as React.CSSProperties,
  agentIntroductionText: {
    margin: 0,
    color: '#334155',
    fontSize: 13,
    lineHeight: 1.5,
  } as React.CSSProperties,
  welcome: {
    background: 'white',
    border: '1px solid #e5e7eb',
    padding: 14,
    borderRadius: 14,
    fontSize: 13,
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  } as React.CSSProperties,
  welcomeIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#eff6ff',
    color: '#2563eb',
    marginBottom: 8,
    fontWeight: 700,
  } as React.CSSProperties,
  userMsg: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: 12 } as React.CSSProperties,
  agentMsg: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 12 } as React.CSSProperties,
  systemMsg: { display: 'flex', justifyContent: 'center', marginBottom: 8 } as React.CSSProperties,
  messageMeta: {
    fontSize: 10,
    color: '#94a3b8',
    marginBottom: 4,
    paddingLeft: 34,
  } as React.CSSProperties,
  messageRow: (role: 'user' | 'agent'): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
    gap: 8,
    width: '100%',
  }),
  avatar: {
    width: 26,
    height: 26,
    borderRadius: 999,
    background: '#e0f2fe',
    color: '#0369a1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    flex: '0 0 auto',
  } as React.CSSProperties,
  timestamp: {
    fontSize: 10,
    color: '#9ca3af',
    marginTop: 2,
    paddingLeft: 4,
    paddingRight: 4,
  } as React.CSSProperties,
  seenLabel: {
    fontSize: 10,
    color: '#2563eb',
    marginTop: 4,
    paddingLeft: 4,
    paddingRight: 4,
  } as React.CSSProperties,
  systemBubble: {
    fontSize: 12,
    color: '#6b7280',
    background: '#f3f4f6',
    padding: '4px 10px',
    borderRadius: 12,
  } as React.CSSProperties,
  bubble: (role: 'user' | 'agent'): React.CSSProperties => ({
    maxWidth: role === 'agent' ? '92%' : '80%',
    padding: '10px 12px',
    borderRadius: role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    background: role === 'user' ? '#0078d4' : 'white',
    color: role === 'user' ? 'white' : '#111827',
    fontSize: 14,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: role === 'user' ? 'none' : '1px solid #e5e7eb',
    boxShadow: role === 'user' ? '0 2px 8px rgba(0,120,212,0.20)' : '0 1px 2px rgba(15,23,42,0.04)',
  }),
  traceBubble: {
    maxWidth: '95%',
    padding: '8px 12px',
    borderRadius: 12,
    background: '#1e1e1e',
    color: '#d4d4d4',
    fontSize: 11,
    fontFamily: 'Consolas, "Courier New", monospace',
    whiteSpace: 'pre',
    overflowX: 'auto',
    lineHeight: 1.4,
  } as React.CSSProperties,
  activity: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#475569',
    minHeight: 22,
  } as React.CSSProperties,
  activityDots: {
    display: 'inline-flex',
    gap: 3,
  } as React.CSSProperties,
  activityDot: {
    width: 5,
    height: 5,
    borderRadius: 999,
    background: '#2563eb',
    animation: 'cmsp-chat-pulse 1.1s ease-in-out infinite',
  } as React.CSSProperties,
  composer: {
    borderTop: '1px solid #e5e7eb',
    padding: 12,
    background: 'white',
  } as React.CSSProperties,
  composerBox: {
    border: '1px solid #d1d5db',
    borderRadius: 14,
    padding: 8,
    background: 'white',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  } as React.CSSProperties,
  textarea: {
    width: '100%',
    padding: '4px 2px 8px',
    border: 'none',
    outline: 'none',
    fontSize: 14,
    fontFamily: 'inherit',
    resize: 'none',
    maxHeight: 128,
    overflowY: 'auto',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  composerFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 } as React.CSSProperties,
  composerHint: { fontSize: 10, color: '#9ca3af' } as React.CSSProperties,
  disclaimer: {
    color: '#9ca3af',
    fontSize: 10,
    marginTop: 6,
    textAlign: 'center',
  } as React.CSSProperties,
  confirmScrim: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(15,23,42,0.18)',
    zIndex: 10,
  } as React.CSSProperties,
  confirmSheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 11,
    background: 'white',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    boxShadow: '0 -12px 40px rgba(15,23,42,0.18)',
    padding: '28px 24px 22px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  } as React.CSSProperties,
  confirmTitle: {
    fontSize: 20,
    fontWeight: 650,
    color: '#111827',
  } as React.CSSProperties,
  confirmBody: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    marginBottom: 6,
  } as React.CSSProperties,
  confirmPrimary: {
    width: '100%',
    background: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 650,
    cursor: 'pointer',
  } as React.CSSProperties,
  confirmSecondary: {
    width: '100%',
    background: 'white',
    color: '#111827',
    border: '1px dashed #111827',
    borderRadius: 6,
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  endedBlock: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 } as React.CSSProperties,
  endedNotice: {
    alignSelf: 'center',
    fontSize: 12,
    color: '#6b7280',
    background: '#eef2f7',
    borderRadius: 999,
    padding: '4px 12px',
  } as React.CSSProperties,
  feedbackPrompt: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 16,
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  } as React.CSSProperties,
  feedbackPromptButton: {
    width: '100%',
    background: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: 6,
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 650,
    cursor: 'pointer',
  } as React.CSSProperties,
  feedbackCard: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 11,
    width: 'calc(100% - 32px)',
    maxWidth: 360,
    maxHeight: 'calc(100% - 40px)',
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 24px 70px rgba(15,23,42,0.28)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as React.CSSProperties,
  feedbackCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 6px',
  } as React.CSSProperties,
  feedbackCardTitle: { fontSize: 17, fontWeight: 650, color: '#0f4c8a' } as React.CSSProperties,
  feedbackScroll: { padding: '6px 16px 12px', overflowY: 'auto' } as React.CSSProperties,
  feedbackQuestion: { fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 } as React.CSSProperties,
  feedbackOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 2px',
    fontSize: 14,
    color: '#111827',
    cursor: 'pointer',
  } as React.CSSProperties,
  feedbackTextarea: {
    width: '100%',
    marginTop: 8,
    padding: 10,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    color: '#111827',
  } as React.CSSProperties,
  feedbackPrivacy: { fontSize: 12, color: '#4b5563', marginTop: 10, lineHeight: 1.4 } as React.CSSProperties,
  feedbackActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '12px 16px 16px',
    borderTop: '1px solid #f1f5f9',
  } as React.CSSProperties,
  feedbackSend: {
    background: '#e5e7eb',
    color: '#111827',
    border: 'none',
    borderRadius: 4,
    padding: '8px 22px',
    fontSize: 14,
    fontWeight: 600,
  } as React.CSSProperties,
  feedbackCancel: {
    background: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '8px 22px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  send: {
    background: 'transparent',
    color: '#0078d4',
    border: 'none',
    padding: 4,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    cursor: 'pointer',
  } as React.CSSProperties,
  stop: {
    background: '#fff1f2',
    color: '#be123c',
    border: '1px solid #fecdd3',
    padding: '7px 14px',
    borderRadius: 999,
    cursor: 'pointer',
    fontWeight: 500,
  } as React.CSSProperties,
  authButton: {
    background: 'white',
    color: '#0078d4',
    border: '1px solid #0078d4',
    padding: '6px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  } as React.CSSProperties,
  handoffCard: {
    marginTop: 8,
    padding: '10px 12px',
    background: '#f3f9fd',
    border: '1px solid #0078d4',
    borderRadius: 6,
  } as React.CSSProperties,
  handoffCardTitle: {
    color: '#0078d4',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  } as React.CSSProperties,
  handoffCardText: {
    color: '#444',
    fontSize: 12,
  } as React.CSSProperties,
  modalityOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 10,
  } as React.CSSProperties,
  modalityButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #0078d4',
    background: '#f3f9fd',
    color: '#0f172a',
    cursor: 'pointer',
    textAlign: 'left',
  } as React.CSSProperties,
  callbackOption: {
    display: 'grid',
    gap: 8,
  } as React.CSSProperties,
  callbackPhoneLabel: {
    display: 'grid',
    gap: 4,
    color: '#323130',
    fontSize: 12,
    fontWeight: 600,
  } as React.CSSProperties,
  callbackPhoneInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #8a8886',
    borderRadius: 4,
    padding: '8px 10px',
    font: 'inherit',
    background: '#ffffff',
    color: '#201f1e',
  } as React.CSSProperties,
  modalityDescription: {
    color: '#475569',
    fontSize: 12,
    fontWeight: 400,
  } as React.CSSProperties,
  classificationCard: {
    display: 'grid',
    gap: 8,
    marginTop: 10,
    padding: 12,
    border: '1px solid #0078d4',
    borderRadius: 8,
    background: '#f3f9fd',
  } as React.CSSProperties,
  classificationLabel: {
    display: 'grid',
    gap: 4,
    color: '#323130',
    fontSize: 12,
    fontWeight: 600,
  } as React.CSSProperties,
  classificationSelect: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #8a8886',
    borderRadius: 4,
    padding: '8px 10px',
    font: 'inherit',
    background: '#ffffff',
    color: '#201f1e',
  } as React.CSSProperties,
  classificationActions: {
    display: 'grid',
    gap: 8,
  } as React.CSSProperties,
  feedbackBar: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
  } as React.CSSProperties,
  feedbackButton: {
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 6,
    padding: '2px 6px',
    cursor: 'pointer',
    fontSize: 13,
    lineHeight: 1.2,
    opacity: 0.55,
    transition: 'opacity 0.15s, background 0.15s, border-color 0.15s',
  } as React.CSSProperties,
  feedbackButtonActive: {
    opacity: 1,
    background: '#e8f0fe',
    borderColor: '#2563eb',
  } as React.CSSProperties,
  feedbackButtonDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  } as React.CSSProperties,
  reasonOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: 16,
  } as React.CSSProperties,
  reasonDialog: {
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    padding: 16,
    width: '100%',
    maxWidth: 320,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  } as React.CSSProperties,
  reasonTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
  } as React.CSSProperties,
  reasonList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  } as React.CSSProperties,
  reasonItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#374151',
    cursor: 'pointer',
  } as React.CSSProperties,
  reasonTextarea: {
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: 8,
    fontSize: 13,
    fontFamily: 'inherit',
  } as React.CSSProperties,
  reasonActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 4,
  } as React.CSSProperties,
  reasonCancel: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#374151',
  } as React.CSSProperties,
  reasonSubmit: {
    background: '#2563eb',
    border: '1px solid #2563eb',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#fff',
    fontWeight: 500,
  } as React.CSSProperties,
  suggestedPrompts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
    paddingLeft: 34,
    maxWidth: '90%',
  } as React.CSSProperties,
  suggestedPromptButton: {
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    borderRadius: 999,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 12,
    fontFamily: 'inherit',
    textAlign: 'left',
  } as React.CSSProperties,
  headerIconButton: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    background: 'white',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
  } as React.CSSProperties,
  layoutMenuWrapper: { position: 'relative' } as React.CSSProperties,
  layoutMenu: {
    position: 'absolute',
    top: 34,
    right: 0,
    minWidth: 150,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    boxShadow: '0 16px 36px rgba(15,23,42,0.16)',
    padding: 6,
    zIndex: 1001,
  } as React.CSSProperties,
  layoutMenuItem: (active: boolean): React.CSSProperties => ({
    width: '100%',
    border: 'none',
    background: active ? '#eff6ff' : 'transparent',
    color: active ? '#1d4ed8' : '#334155',
    borderRadius: 8,
    padding: '8px 10px',
    cursor: 'pointer',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
  }),
};
